// FlangeForm.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Dialog,
  CardHeader,
  CardContent,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Chip,
  Button,
  Collapse,
  Tooltip,
  IconButton,
  Modal,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { IoIosInformationCircleOutline } from "react-icons/io";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Download as DownloadIcon, CloudUpload as UploadIcon } from "@mui/icons-material";
import { IoCalendarOutline } from "react-icons/io5";
import { IoChatbubble } from "react-icons/io5";
import { LuTimer } from "react-icons/lu";
import { FiClock } from "react-icons/fi";
import { FaHistory } from "react-icons/fa";
import axiosInstance from "../axiosinstance";
import API_URL from "../Config";
import FormComponent from "./FormComponent";
import "bootstrap/dist/css/bootstrap.min.css";
import "./FlangeForm.css";
// import { useTestingSession } from "./TestingSessionContext";

/** Canonical step order shown in tabs */
const ALL_STEPS = [
  "Flange Blinding",
  "Flange Deblinding",
  "Flange Tightening",
  "Flange Testing",
];

// ----------------------------------------
// Testing session persistence (localStorage)
// ----------------------------------------
const TESTING_SESSION_KEY = (flangeId) => `flange_testing_session_${flangeId}`;

/** -----------------------------
 * Schema for each visible tab
 * ----------------------------- */
function getSchemaForFormName(formName) {
  switch (formName) {
    case "Flange Blinding":
      return {
        key: "blinding",
        title: "Flange Blinding",
        headerFields: [
          { name: "equipment_number", label: "Equipment No" },
          { name: "user_filling_the_form", label: "Name" },
          { name: "signed", label: "Signed" },
          { name: "method_of_tightening", label: "Method of Tightening" },
        ],
        fields: [
          {
            name: "remarks",
            label: "Remarks",
            type: "multiline",
            rows: 3,
            grid: 12,
          },
          {
            name: "__uploads",
            label: "Upload Photos",
            type: "upload",
            multiple: true,
            grid: 12,
          },
        ],
        aliases: { remarks: "remark" },
        canUpload: true,
        editEndpoint: "edit_blinding_form",
      };

    case "Flange Deblinding":
      return {
        key: "deblinding",
        title: "Flange Deblinding",
        headerFields: [
          { name: "equipment_number", label: "Equipment No" },
          { name: "user_filling_the_form", label: "Name" },
          { name: "signed", label: "Signed" },
          { name: "method_of_tightening", label: "Method of Tightening" },
        ],
        fields: [
          {
            name: "remarks",
            label: "Remarks",
            type: "multiline",
            rows: 3,
            grid: 12,
          },
          {
            name: "__uploads",
            label: "Upload Photos",
            type: "upload",
            multiple: true,
            grid: 12,
          },
        ],
        aliases: { remarks: "remark" },
        canUpload: true,
        editEndpoint: "edit_deblinding_form",
      };

    case "Flange Tightening":
      return {
        key: "tightening",
        title: "Flange Tightening",
        headerFields: [
          { name: "equipment_number", label: "Equipment No" },
          { name: "user_filling_the_form", label: "Name" },
          { name: "signed", label: "Signed" },
          { name: "method_of_tightening", label: "Method of Tightening" },
        ],
        fields: [
          { name: "pass1", label: "Pass 1", type: "number", grid: 6, md: 3 },
          { name: "pass2", label: "Pass 2", type: "number", grid: 6, md: 3 },
          { name: "pass3", label: "Pass 3", type: "number", grid: 6, md: 3 },
          { name: "pass4", label: "Pass 4", type: "number", grid: 6, md: 3 },
          {
            name: "is_extra_tightening",
            label: "Is Extra Tightening?",
            type: "checkbox",
            grid: 12,
          },
          {
            name: "tightening_remark",
            label: "Extra - Tightening Remark*",
            type: "text",
            grid: 12,
            visibleWhen: (vals) =>
              !!vals?.is_extra_tightening || !!vals?.tightening_remark,
            required: (vals) =>
              !!vals?.is_extra_tightening 
          },
          {
            name: "gap_between_faces",
            label: "Gap Between Faces",
            type: "number",
            grid: 12,
            md: 6,
          },
          {
            name: "remarks",
            label: `Remarks`,
            type: "multiline",
            grid: 12,
            md: 6,
            
          },
          {
            name: "__uploads",
            label: "Upload Photos",
            type: "upload",
            multiple: true,
            grid: 12,
          },
        ],
        aliases: { remarks: "remark" },
        canUpload: true,
        editEndpoint: "edit_tightening_form",
      };

    case "Flange Testing":
      return {
        key: "testing",
        title: "Flange Testing",
        headerFields: [
          { name: "equipment_number", label: "Equipment No" },
          { name: "user_filling_the_form", label: "Name" },
          { name: "signed", label: "Signed" },
          { name: "method_of_tightening", label: "Method of Tightening" },
        ],
        fields: [
          {
            name: "pressure_value",
            label: "Pressure Value",
            type: "number",
            grid: 6,
          },
          {
            name: "test_result",
            label: "Test Result",
            type: "select",
            required: true,
            options: [
              { label: "PASS", value: "PASS" },
              { label: "FAIL", value: "FAIL" },
            ],
            grid: 6,
          },
          {
            name: "remarks",
            label: "Remarks",
            type: "multiline",
            grid: 6,
          },
          {
            name: "__gauge_photo",
            label: "Upload Gauge Photo",
            type: "upload",
            multiple: false,
            grid: 6,
            required: true,
            helpText: "* Gauge photo is required for testing",
          },
          {
            name: "__uploads",
            label: "Upload Photos",
            type: "upload",
            multiple: true,
            grid: 6,
          },
        ],
        aliases: {
          pressure_value: "pressure_value",
          test_result: "test_result",
          remarks: "remark",
        },
        canUpload: true,
        hideSubmit: true, // Testing & Retesting save on End
        editEndpoint: "edit_testing_form",
      };

    default:
      return {
        key: "unknown",
        title: formName || "Unknown Form",
        headerFields: [],
        fields: [],
        aliases: {},
        canUpload: false,
      };
  }
}

const uniqueOrdered = (arr) => {
  const out = [];
  const seen = new Set();
  (arr || []).forEach((x) => {
    if (x && !seen.has(x)) {
      seen.add(x);
      out.push(x);
    }
  });
  return out;
};

const normalizeFormForUI = (name) => {
  switch (name) {
    case "Approval from Client":
    case "Flange Retesting":
    case "Retesting":
      return "Flange Testing";
    case "Flange Retightening":
      return "Flange Tightening";
    default:
      return name || "";
  }
};

const getTorqueWarning = (finalCheck, requiredTorque) => {
  if (
    finalCheck === "" ||
    finalCheck === null ||
    finalCheck === undefined
  ) {
    return "";
  }

  const finalValue = Number(finalCheck);
  const requiredValue = Number(requiredTorque);

  if (
    !Number.isFinite(finalValue) ||
    !Number.isFinite(requiredValue) ||
    requiredValue <= 0
  ) {
    return "";
  }

  const lowerBound = requiredValue * 0.95;
  const upperBound = requiredValue * 1.05;

  if (finalValue < lowerBound) {
    return `Under-tightened: ${finalValue} Nm < ${lowerBound.toFixed(1)} Nm. May cause leakage.`;
  }

  if (finalValue > upperBound) {
    return `Over-tightened: ${finalValue} Nm > ${upperBound.toFixed(1)} Nm. May damage bolts/flange.`;
  }

  return "";
};

const getApiErrorMessage = (error, fallback) => {
  const payload = error?.response?.data;
  const detail = payload?.error ?? payload?.message ?? payload?.detail;

  if (typeof detail === "string" && detail.trim()) return detail.trim();
  if (Array.isArray(detail)) return detail.filter(Boolean).join(", ");
  return fallback;
};

const inferFieldFromApiError = (message = "") => {
  const normalized = String(message).toLowerCase();
  const passMatch = normalized.match(/\bpass\s*([1-4])\b/);
  if (passMatch) return `pass${passMatch[1]}`;
  if (normalized.includes("test result")) return "test_result";
  if (normalized.includes("gauge photo")) return "__gauge_photo";
  if (normalized.includes("pressure")) return "pressure_value";
  if (normalized.includes("remark")) return "remarks";
  return null;
};

const isTorqueInputField = (fieldName = "") => {
  const name = String(fieldName).toLowerCase();
  if (!name) return false;
  if (name === "gap_between_faces") return false;
  return name.includes("pass") || name.includes("final_check");
};

const isFlangeReopened = (detailLike) => {
  const s =
    (detailLike?.status ||
      detailLike?.form_info?.status ||
      detailLike?.flange_status ||
      "") + "";
  return /flange\s*reopened/i.test(s);
};

// -------- it will showing all tab -------- //
const buildStepsFromFormInfo = (_form_info, isReopened) => {
  // When reopened, return only up to current form regardless of previous_forms
  if (isReopened) {
    const currentFormNormalized = normalizeFormForUI(_form_info?.current_form);
    // Only return current form when reopened
    return currentFormNormalized ? [currentFormNormalized] : [];
  }
  return ALL_STEPS.slice();
};

/** Max tab index the user may access, based on history + current_form */
const calcUnlockedIndex = (form_info, options = {}) => {
  const order = ALL_STEPS;
  const forceFromCurrent = options?.forceFromCurrentOnly;

  // If forcing from current form only (reopened case)
  if (forceFromCurrent) {
    const curr = normalizeFormForUI(form_info?.current_form || "");
    return curr ? order.indexOf(curr) : 0;
  }

  const prev = Array.isArray(form_info?.previous_forms)
    ? form_info.previous_forms
    : [];
  const curr = form_info?.current_form ? [form_info.current_form] : [];

  const done = uniqueOrdered(
    [...prev, ...curr]
      .filter((n) => n && n !== "Approval from Client")
      .map(normalizeFormForUI),
  );

  const indices = done.map((n) => order.indexOf(n)).filter((i) => i >= 0);
  return indices.length ? Math.max(...indices) : 0;
};

const sectionKeyToSchemaKey = (sectionKey) => {
  switch ((sectionKey || "").toLowerCase()) {
    // ✅ Blinding keys (handle all possible backend keys)
    case "blinding":
    case "blind":
    case "blinded":
      return "blinding";

    // ✅ Deblinding keys
    case "deblinding":
    case "deblind":
    case "deblinded":
      return "deblinding";

    case "tightend":
    case "tightening": // ✅ safety
      return "tightening";

    case "tested":
    case "testing": // ✅ safety
      return "testing";

    default:
      return null;
  }
};

const formNameToHistorySection = (formName) => {
  switch (formName) {
    case "Flange Tightening":
      return "tightend";
    case "Flange Retightening":
      return "tightend";
    case "Flange Testing":
      return "tested";
    default:
      return null;
  }
};

const normalizeHistoryToSnapshots = (historyPayload) => {
  const snapshots = {};
  const sections = Array.isArray(historyPayload?.data)
    ? historyPayload.data
    : [];

  sections.forEach((sectionObj) => {
    const sectionKey = Object.keys(sectionObj || {})[0];
    const formKey = sectionKeyToSchemaKey(sectionKey);
    // console.log("🔑 Section Key:", sectionKey, "→ formKey:", formKey); 
    if (!formKey) return;

    const entries = sectionObj[sectionKey];
    if (!Array.isArray(entries) || entries.length === 0) return;

    // ✅ CHANGE 1: For testing, find by 'latest' flag OR highest cycle
    let recordEntry;
    if (formKey === "testing") {
      // 🔍 First priority: Find entry with latest === true
      recordEntry = entries.find((e) => e.latest === true);

      // console.log("🔍 Testing entries search:", {
      //   totalEntries: entries.length,
      //   foundLatest: !!recordEntry,
      //   latestEntry: recordEntry
      //     ? {
      //         type: recordEntry.type,
      //         cycle: recordEntry.testing_cycle_number,
      //         retest_id: recordEntry.retest_id,
      //         green_id: recordEntry.green_id,
      //       }
      //     : null,
      // });

      // Fallback: Get highest cycle number if no latest flag
      if (!recordEntry) {
        recordEntry = entries.reduce((max, current) => {
          const currentCycle = current?.testing_cycle_number ?? 0;
          const maxCycle = max?.testing_cycle_number ?? 0;
          return currentCycle > maxCycle ? current : max;
        }, entries[0]);
      }
    } else {
      recordEntry =
        entries.find((e) => e.latest) ||
        entries.find(
          (e) =>
            e.blue_id ||
            e.red_id ||
            e.yellow_id ||
            e.green_id ||
            e.retightening_id ||
            e.retesting_id ||
            e.type,
        ) ||
        entries[entries.length - 1];
    }
    // console.log("📦 recordEntry for", formKey, ":", recordEntry); // ADD THIS
    // console.log("📁 uploaded_files:", recordEntry?.uploaded_files);  // ADD THIS
    const snap = {};
    snap.remarks = recordEntry?.remarks ?? recordEntry?.remark ?? "";

    [
      "pass1",
      "pass2",
      "pass3",
      "pass4",
      "gap_between_faces",
      "is_extra_tightening",
      "tightening_remark",
    ].forEach((k) => {
      if (recordEntry?.[k] !== undefined) snap[k] = recordEntry[k];
    });

    if (recordEntry?.pressure_value !== undefined)
      snap.pressure_value = recordEntry.pressure_value;
    if (recordEntry?.test_result !== undefined)
      snap.test_result = recordEntry.test_result;

    const existingGauge = [];
    const existingUploads = [];

    if (Array.isArray(recordEntry?.gauge_photo)) {
      recordEntry.gauge_photo.forEach((f) =>
        existingGauge.push({
          file_id: f.file_id ?? f.id,
          file_path: f.file_path,
          filename: f.filename,
          file_size: f.file_size,
        }),
      );
    }
    if (Array.isArray(recordEntry?.uploaded_files)) {
      recordEntry.uploaded_files.forEach((f) =>
        existingUploads.push({
          file_id: f.file_id ?? f.id,
          file_path: f.file_path,
          filename: f.filename,
          file_size: f.file_size,
        }),
      );
    }

    snap.__existingGauge = existingGauge;
    snap.__existingUploads = existingUploads;

    // ✅ CHANGE 2 (FIXED): Capture correct recordId + recordType per formKey
    // ✅ FIXED: Capture correct recordId + recordType per formKey
    if (formKey === "testing") {
      // ✅ Identify record type from backend history
      const entryType = (recordEntry?.type || "").toLowerCase();

      if (entryType === "retesting") {
        // ✅ Retesting record → use retest_id (primary key for retesting)
        snap.__recordId = recordEntry?.retest_id || "";
        snap.__recordType = "retesting";

        
      } else {
        // ✅ Normal Testing record → use green_id (primary key for testing)
        snap.__recordId = recordEntry?.green_id || "";
        snap.__recordType = "testing";

        
      }
    } else if (formKey === "tightening") {
      // ✅ Tightening cycles use blue/red/yellow IDs
      if (recordEntry?.blue_id) {
        snap.__recordId = recordEntry.blue_id;
        snap.__recordType = "tightening";
      } else if (recordEntry?.red_id) {
        snap.__recordId = recordEntry.red_id;
        snap.__recordType = "tightening";
      } else if (recordEntry?.yellow_id) {
        snap.__recordId = recordEntry.yellow_id;
        snap.__recordType = "tightening";
      } else if (recordEntry?.retightening_id) {
        snap.__recordId = recordEntry.retightening_id;
        snap.__recordType = "retightening";
      } else if (recordEntry?.id) {
        snap.__recordId = recordEntry.id;
        snap.__recordType = "tightening";
      }
    } else if (formKey === "blinding") {
      // ✅ Blinding uses blue_id
      snap.__recordId = recordEntry?.blue_id || recordEntry?.id || "";
      snap.__recordType = "blinding";
    } else if (formKey === "deblinding") {
      // ✅ Deblinding uses red_id
      snap.__recordId = recordEntry?.red_id || recordEntry?.id || "";
      snap.__recordType = "deblinding";
    } else {
      // ✅ fallback
      if (recordEntry?.id) {
        snap.__recordId = recordEntry.id;
        snap.__recordType = formKey;
      }
    }
    //  console.log("✅ Final snap.__existingUploads for", formKey, ":", snap.__existingUploads); // ADD THIS
    snapshots[formKey] = snap;
  });

  return snapshots;
};

/** Prefer non-empty (not null/undefined/"") values when merging snapshots */
const mergeSnapshotsPreferNonEmpty = (prev = {}, next = {}) => {
  
  const out = { ...prev };
  Object.keys(next).forEach((k) => {
    // ✅ Always overwrite file list arrays from fresh history fetch
    if (k === "__existingUploads" || k === "__existingGauge") {
      out[k] = next[k]; // always take the fresh value
      return;
    }

    const v = next[k];
    const isEmpty =
      v === null ||
      v === undefined ||
      (typeof v === "string" && v.trim() === "");
    if (!isEmpty) {
      out[k] = v;
    } else if (out[k] === undefined) {
      out[k] = v;
    }
  });
  return out;
};

// helper for start/end timestamps -> "Xh Ym Zs
/** Get user who filled a specific form from history */
const getUserFromHistory = (rawHistory, schemaKey) => {
  if (!rawHistory || !schemaKey) return null;

  // Map schema key to history section key
  const historyKeyMap = {
    blinding: "blinding",
    deblinding: "deblinding",
    tightening: "tightend",
    testing: "tested",
  };

  const sectionKey = historyKeyMap[schemaKey];
  if (!sectionKey) return null;

  const sectionObj = (rawHistory || []).find(
    (x) => Object.keys(x || {})[0] === sectionKey,
  );

  if (!sectionObj) return null;

  const entries = sectionObj[sectionKey];
  if (!Array.isArray(entries) || entries.length === 0) return null;

  // Get the latest entry or last entry
  const entry = entries.find((e) => e.latest) || entries[entries.length - 1];
  return entry?.user_who_filled_the_data || null;
};

const thumbUrl = (API_URL, filePath = "") => {
  if (!filePath) return "";

  const cleaned = filePath.replace(/\\/g, "/").replace(/^\/+/, "");

  // ✅ if backend sends "uploads/..." then DIRECTLY use it (NO encoding)
  if (cleaned.startsWith("uploads/")) {
    // ✅ remove "uploads/" prefix
    return `${API_URL}/view_image/${cleaned.replace(/^uploads\//, "")}`;
  }

  const parts = cleaned.split("/").filter(Boolean);

  // case: folder/file.jpg
  if (parts.length === 2) {
    return `${API_URL}/view_image/${parts[0]}/${parts[1]}`;
  }

  // case: only file.jpg
  if (parts.length === 1) {
    return `${API_URL}/view_image/uploads/${parts[0]}`;
  }

  // ✅ IMPORTANT CHANGE:
  // for folder/uuid/filename.jpg => DO NOT encode full filename because it contains / sometimes
  if (parts.length >= 3) {
    return `${API_URL}/view_image/${parts.join("/")}`;
  }

  return "";
};

const buildDownloadUrl = (API_URL, filePath = "") => {
  if (!filePath) return "";

  const cleaned = filePath.replace(/\\/g, "/").replace(/^\/+/, "");

  if (cleaned.startsWith("uploads/")) {
    return `${API_URL}/download_image/${cleaned.replace(/^uploads\//, "")}`;
  }
  const parts = cleaned.split("/").filter(Boolean);

  if (parts.length === 2) {
    return `${API_URL}/download_image/${parts[0]}/${parts[1]}`;
  }

  if (parts.length === 1) {
    return `${API_URL}/download_image/uploads/${parts[0]}`;
  }

  if (parts.length >= 3) {
    return `${API_URL}/download_image/${parts.join("/")}`;
  }

  return "";
};

function HistoryPanel({
  sectionEntries = [],
  API_URL,
  currentForm = "",
  approvalAttachments = [],
  approvalDetails = {},
}) {
  const [expanded, setExpanded] = React.useState({});
  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [downloadSnack, setDownloadSnack] = useState({ open: false, message: "" });

  const toHMS = (d) =>
    d
      ? new Date(d).toLocaleTimeString("en-GB", {
          hour12: false,
          timeZone: "UTC",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
      : "-";

  const toDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

  // Duration between start/end in HH:MM:SS
  const formatDurationHMS = (start, end) => {
    if (!start || !end) return "-";
    const ms = Math.max(0, new Date(end) - new Date(start));
    const s = Math.floor(ms / 1000);
    const h = String(Math.floor(s / 3600)).padStart(2, "0");
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${h}:${m}:${sec}`;
  };
  //old api for downloading image.
  // const thumbUrl = (p = "") => `${API_URL}/${(p || "").replace(/\\/g, "/")}`;
  // const buildDownloadUrl = (p = "") =>
  //   `${API_URL}/api/download?path=${encodeURIComponent(p || "")}`;

  const downloadWithAuth = async (filePath, filename) => {
    try {
      const token = localStorage.getItem("authKey");
      const downloadUrl = buildDownloadUrl(API_URL, filePath);

      const res = await axiosInstance.get(downloadUrl, {
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename || filePath.split(/[\\/]/).pop();
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      
      console.error("Download error:", e);
      alert("Could not download file.");
    }
  };

  const handleImageOpen = async (imageSrcOrNull, filePath) => {
    
    if (imageSrcOrNull) {
      console.log("Opening image directly from existing URL:", imageSrcOrNull);
      setSelectedImage(imageSrcOrNull);
      setOpenImage(true);
      return;
    }

    try {
      const viewUrl = thumbUrl(API_URL, filePath);
      const token = localStorage.getItem("authKey");

      const res = await axiosInstance.get(viewUrl, {
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      const blob = new Blob([res.data]);
      const objectUrl = URL.createObjectURL(blob);

      setSelectedImage(objectUrl);
      setOpenImage(true);
    } catch (err) {
      console.error("Failed to open image:", err);
      alert("Could not load image.");
    }
  };

  const handleImageClose = () => {
    setOpenImage(false);
    // Don't revoke here - the thumbnail might still be using it
    // The blob URL will be cleaned up when the thumbnail unmounts
    setSelectedImage(null);
  };

  const getHistoryTitle = () => {
    if (!Array.isArray(sectionEntries) || sectionEntries.length === 0) {
      return "History";
    }

    const firstEntry = sectionEntries[0] || {};
    const type = (firstEntry.type || "").toString().toLowerCase();

    // Prefer type if available
    if (type.includes("tighten")) return "Tightening History";
    if (type.includes("test")) return "Testing History";

    // Fallback to cycle fields if type not set
    if (firstEntry.tightening_cycle_number != null) return "Tightening History";
    if (firstEntry.testing_cycle_number != null) return "Testing History";

    return "History";
  };

  if (!sectionEntries || sectionEntries.length === 0) {
    return (
      <Box className="flange-history-panel">
        <Box className="flange-history-header">
          <Typography variant="subtitle1" fontWeight="600">
            <FaHistory /> {getHistoryTitle()}
          </Typography>
        </Box>
        <Box className="flange-history-empty">
          <Typography variant="body2" color="text.secondary">
            No history !
          </Typography>
        </Box>
      </Box>
    );
  }

  const items = (Array.isArray(sectionEntries) ? sectionEntries : []).map(
    (it) => {
      const typeRaw = (it?.type || "").toString().trim();
      const typeLower = typeRaw.toLowerCase();

      const isTestingEntry =
        typeLower.includes("test") ||
        it.test_result !== undefined ||
        it.pressure_value !== undefined;

      const isTighteningEntry =
        !isTestingEntry &&
        (it.pass1 !== undefined ||
          it.pass2 !== undefined ||
          it.pass3 !== undefined ||
          it.pass4 !== undefined);

      // Tightening specific values
      const tighteningRemark = it.is_extra_tightening
        ? it.tightening_remark || "-"
        : null;

      const passValuesObj = {
        p1: it.pass1 ?? "-",
        p2: it.pass2 ?? "-",
        p3: it.pass3 ?? "-",
        p4: it.pass4 ?? "-",
      };

      // timestamps
      const startTs = isTestingEntry
        ? it.start_time_for_duration || null
        : null;
      const endTs = isTestingEntry ? it.end_time_for_duration || null : null;

      // Backend cycle numbers (NO frontend iteration)
      const cycleNumber = isTestingEntry
        ? (it.testing_cycle_number ?? null)
        : (it.tightening_cycle_number ?? null);

      // Human label from backend type
      const typeLabel =
        typeRaw.length > 0
          ? typeRaw.charAt(0).toUpperCase() + typeRaw.slice(1)
          : "";

      // Final status label (e.g. "Tightening #4", "Retesting #3")
      const statusLabel = cycleNumber
        ? typeLabel
          ? `${typeLabel}`
          : `#${cycleNumber}`
        : typeLabel;

      const passValues =
        it.pass1 !== undefined
          ? `P1: ${it.pass1 ?? "-"}, P2: ${it.pass2 ?? "-"}, P3: ${
              it.pass3 ?? "-"
            }, P4: ${it.pass4 ?? "-"}`
          : "";

      const gap =
        it.gap_between_faces !== undefined && it.gap_between_faces !== null
          ? `${it.gap_between_faces}`
          : "";

      const testPressure =
        it.pressure_value !== undefined && it.pressure_value !== null
          ? `${it.pressure_value}`
          : "";

      const _gauge = Array.isArray(it.gauge_photo) ? it.gauge_photo : [];
      const _uploads = Array.isArray(it.uploaded_files)
        ? it.uploaded_files
        : [];

      return {
        // ---------- DATE ----------
        date: isTestingEntry
          ? toDate(startTs || endTs || it.record_date_at_timezone)
          : toDate(it.record_date_at_timezone),
        startTime:
          isTestingEntry && startTs
            ? (() => {
                // Remove timezone suffix (last word) before parsing
                const cleanDate = startTs.replace(/\s+[A-Z]{3,4}$/, "").trim();
                const d = new Date(cleanDate);
                const day = String(d.getDate()).padStart(2, "0");
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const year = d.getFullYear();
                const time = d.toLocaleTimeString("en-GB", { hour12: false });
                return `${day}/${month}/${year} Time: ${time}`;
              })()
            : "-",

        endTime:
          isTestingEntry && endTs
            ? (() => {
                // Remove timezone suffix (last word) before parsing
                const cleanDate = endTs.replace(/\s+[A-Z]{3,4}$/, "").trim();
                const d = new Date(cleanDate);
                const day = String(d.getDate()).padStart(2, "0");
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const year = d.getFullYear();
                const time = d.toLocaleTimeString("en-GB", { hour12: false });
                return `${day}/${month}/${year} Time: ${time}`;
              })()
            : "-",

        // ADD THIS LINE:
        duration: it.duration || "-",

        // ---------- COMMON ----------
        statusLabel,
        remarks: it.remarks || "-",

        gauge_photo: _gauge,
        uploaded_files: _uploads,

        // ---------- TESTING ----------
        isTestingEntry,
        testPressure,
        // durationText:
        //   isTestingEntry && startTs ? formatDurationHMS(startTs, endTs) : null,

        testResultLabel:
          isTestingEntry && it.test_result
            ? it.test_result.toUpperCase()
            : null,

        // ---------- TIGHTENING (NEW CLEAN FIELDS) ----------
        isTighteningEntry,
        recordDate: it.record_date_at_timezone
          ? (() => {
              const d = new Date(it.record_date_at_timezone);
              const day = String(d.getDate()).padStart(2, "0");
              const month = String(d.getMonth() + 1).padStart(2, "0");
              const year = d.getFullYear();
              const time = d.toLocaleTimeString("en-GB", { hour12: false });
              // Return JSX for bold time
              return (
                <span>
                  {day}/{month}/{year} Time: <strong>{time}</strong>
                </span>
              );
            })()
          : "-",
        recordDateLabel: it.record_date_at_timezone
          ? new Date(it.record_date_at_timezone).toLocaleDateString("en-GB")
          : "-",
        recordTimeLabel: it.record_date_at_timezone
          ? new Date(it.record_date_at_timezone).toLocaleTimeString("en-GB", {
              hour12: false,
            })
          : "-",

        passValuesObj, // NEW {p1,p2,p3,p4}
        tighteningRemark, // NEW
        extraTightening: it.is_extra_tightening === true, // NEW
        gap: it.gap_between_faces ?? "-", // consistent with tightening

        // ---------- META ----------
        isLatest: !!it.latest, // backend tells which is current
        cycleNumber, // backend cycle number
        type: typeRaw, // tightening / retightening / testing / retesting
      };
    },
  );

  return (
    <Box className="flange-history-panel">
      <Box className="flange-history-header">
        <Typography variant="subtitle1" fontWeight="600">
          <FaHistory /> {getHistoryTitle()}
        </Typography>
      </Box>
      <Box className="flange-history-scroll">
        <Box className="flange-history-timeline">
          {items.map((it, i) => (
            <Paper
              key={i}
              elevation={0}
              className={`flange-history-item${it.isLatest ? " is-current" : ""}`}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={1}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="600">
                    Iteration {it.cycleNumber}
                  </Typography>

                  {it.isLatest && (
                    <Chip
                      label="Current"
                      size="small"
                      sx={{
                        backgroundColor: "#e1261c",
                        color: "white",
                        height: "20px",
                        fontSize: "0.7rem",
                        fontWeight: "600",
                        borderRadius: 1,
                      }}
                    />
                  )}
                </Box>

                <Chip
                  size="small"
                  label={it.statusLabel}
                  color="primary"
                  sx={{
                    height: "23px",
                    backgroundColor: "unset",
                    color: "#6b3440",
                    fontSize: "12px",
                    paddingX: 1.1,
                    paddingY: 1,
                    fontWeight: "600",
                    borderRadius: 1,
                  }}
                />
              </Box>
              {it.isTestingEntry && it.testResultLabel && (
                <Box
                  sx={{
                    display: "inline-block",
                    backgroundColor:
                      it.testResultLabel === "FAIL" ? "red" : "green",
                    color: "#ffffff",
                    fontSize: "12px",
                    fontWeight: 700,
                    px: 1.2,
                    py: 0.3,
                    borderRadius: "12px",
                    my: 1,
                  }}
                >
                  {it.testResultLabel}
                </Box>
              )}

              <Box className="flange-history-datetime">
                <Box display="flex" alignItems="center" gap={0.75}>
                  <IoCalendarOutline size={14} />
                  <Typography variant="caption">{it.recordDateLabel}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.75}>
                  <FiClock size={14} />
                  <Typography variant="caption">{it.recordTimeLabel}</Typography>
                </Box>
              </Box>

              {/* TESTING ONLY */}
              {it.isTestingEntry && (
                <Box mt={1}>
                  {/* START TIME */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <FiClock size={15} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, fontSize: "12px" }}
                    >
                      Start Time:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "12px", fontWeight: 500 }}
                    >
                      {it.startTime || "-"}
                    </Typography>
                  </Box>

                  {/* END TIME */}
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    mt={0.5}
                    sx={{ paddingLeft: "25px" }} // aligns perfectly under Start Time
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, fontSize: "12px" }}
                    >
                      End Time:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "12px", fontWeight: 500 }}
                    >
                      {it.endTime || "-"}
                    </Typography>
                  </Box>

                  {/* TIME DURATION */}
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    <LuTimer size={16} />
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, fontSize: "12px" }}
                    >
                      Duration:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "12px", fontWeight: 500 }}
                    >
                      {it.duration || "-"}
                    </Typography>
                  </Box>
                </Box>
              )}

              <Box mt={1}>
                <Button
                  className="flange-history-details-button"
                  size="small"
                  variant="text"
                  onClick={() => setExpanded((p) => ({ ...p, [i]: !p[i] }))}
                  endIcon={expanded[i] ? <ExpandLess /> : <ExpandMore />}
                  sx={{
                    fontSize: "0.7rem",
                    p: "10px",
                    minWidth: "auto",
                    textTransform: "none",
                    color: "#e1261c",
                  }}
                >
                  DETAILS
                </Button>

                <Collapse in={!!expanded[i]}>
                  <Box mt={1} p={1.5} sx={{ borderRadius: 1 }}>
                    {/* Tightening mapping */}
                    {it.isTighteningEntry && (
                      <Box>
                        {/* PASS VALUES */}
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, fontSize: "12px", mb: 0.5 }}
                        >
                          Pass Values
                        </Typography>

                        <Box
                          mt={0.5}
                          display="grid"
                          gridTemplateColumns="repeat(2, auto)"
                          gap={1}
                          sx={{ fontSize: "12px" }}
                        >
                          <span>
                            Pass 1: <strong>{it.passValuesObj.p1}</strong>
                          </span>
                          <span>
                            Pass 2: <strong>{it.passValuesObj.p2}</strong>
                          </span>
                          <span>
                            Pass 3: <strong>{it.passValuesObj.p3}</strong>
                          </span>
                          <span>
                            Pass 4: <strong>{it.passValuesObj.p4}</strong>
                          </span>
                        </Box>

                        {/* GAP */}
                        <Divider sx={{ my: 1 }} />

                        <Box display="flex" gap={1}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, fontSize: "12px" }}
                          >
                            Gap Between Faces:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "12px" }}
                          >
                            {it.gap}
                          </Typography>
                        </Box>

                        {/* NORMAL REMARK */}

                        <Box mt={1}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, fontSize: "12px" }}
                          >
                            Remark:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "12px", ml: 0.5 }}
                          >
                            {it.remarks}
                          </Typography>
                        </Box>

                        {/* EXTRA TIGHTENING REMARK */}
                        <Box mt={1}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, fontSize: "12px" }}
                          >
                            Extra Tightening Remark:
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ fontSize: "12px", ml: 0.5 }}
                          >
                            {it.tighteningRemark || "-"}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* Testing Histroy */}
                    {it.isTestingEntry && (
                      <Box>
                        {/* REMARKS */}
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, fontSize: "12px" }}
                        >
                          Remarks:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "12px", ml: 0.5 }}
                        >
                          {it.remarks || "-"}
                        </Typography>

                        <Divider sx={{ my: 1 }} />

                        {/* TEST PRESSURE VALUE */}
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, fontSize: "12px" }}
                        >
                          Pressure Value:
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "12px", ml: 0.5 }}
                        >
                          {it.testPressure || "-"}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>

              {/* Gauge Photo (single) */}
              {Array.isArray(it.gauge_photo) && it.gauge_photo.length > 0 && (
                <Box mt={1}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Gauge Photo
                  </Typography>
                  <Box mt={0.5} display="flex" gap={0.75} flexWrap="wrap">
                    {it.gauge_photo.slice(0, 1).map((f, idx) => {
                      const isImg = /\.(png|jpe?g|webp|gif|bmp)$/i.test(
                        f?.file_path || "",
                      );
                      
                      return isImg ? (
                        <AuthImage
                          key={`g-${idx}`}
                          filePath={f.file_path}
                          API_URL={API_URL}
                          onView={handleImageOpen}
                          onDownload={downloadWithAuth}
                          filename={f.filename}
                        />
                      ) : (
                        <Box
                          key={`g-${idx}`}
                          sx={{
                            width: 70,
                            height: 70,
                            borderRadius: 1,
                            overflow: "hidden",
                            bgcolor: "#f2f2f2",
                            border: "1px solid #e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          📄
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {/* Uploaded Photos (multiple) */}
              {Array.isArray(it.uploaded_files) &&
                it.uploaded_files.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Uploaded Photos
                    </Typography>
                    <Box mt={0.5} display="flex" gap={0.75} flexWrap="wrap">
                      {it.uploaded_files.map((f, idx) => {
                        const isImg = /\.(png|jpe?g|webp|gif|bmp)$/i.test(
                          f?.file_path || "",
                        );
                        return (
                          <Box
                            key={`u-${idx}`}
                            sx={{
                              width: 70,
                              height: 70,
                              borderRadius: 1,
                              overflow: "hidden",
                              bgcolor: "#f2f2f2",
                              border: "1px solid #e0e0e0",
                              position: "relative",
                              "&:hover .overlay": {
                                opacity: 1,
                                visibility: "visible",
                              },
                            }}
                          >
                            {isImg ? (
                              <AuthImage
                                key={`u-${idx}`}
                                filePath={f.file_path}
                                API_URL={API_URL}
                                onView={handleImageOpen}
                                onDownload={downloadWithAuth}
                                filename={f.filename}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                📄
                              </Box>
                            )}
                            <Box
                              className="overlay"
                              sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                gap: 0.5,
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "rgba(0,0,0,0.5)",
                                opacity: 0,
                                visibility: "hidden",
                                transition: "opacity .2s",
                              }}
                            >
                              <IconButton
                                size="small"
                                sx={{ color: "#fff" }}
                                onClick={() =>{
                                  
                                  handleImageOpen(null, f.file_path)
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>

                              <IconButton
                                type="button"
                                size="small"
                                sx={{ color: "#fff" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  downloadWithAuth(f.file_path, f.filename);
                                }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
            </Paper>
          ))}
        </Box>

        {/* Approval from Client Attachments - Visually Separate Section */}
        {approvalAttachments && approvalAttachments.length > 0 && (
          <Box sx={{ mt: 3, pt: 2, borderTop: "2px solid #e0e0e0" }}>
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                width: "100%",
                overflow: "hidden",
                backgroundColor: "#fafbfc"
              }}
            >
              {/* Debug log */}
              {/* {console.log("📋 APPROVAL ATTACHMENTS in HistoryPanel:", approvalAttachments)} */}

              {/* Header with file count */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography fontWeight="bold" variant="subtitle1" sx={{ color: "#2196F3" }}>
                  ✓ Approval from Client Attachments
                </Typography>
                <Chip
                  label={`${approvalAttachments.length} file${approvalAttachments.length !== 1 ? "s" : ""}`}
                  size="small"
                  color="primary"
                  variant="filled"
                />
              </Box>

              {/* Details Section */}
              <Box sx={{ mb: 2, pb: 1, borderBottom: "1px solid #e0e0e0" }}>
                <Typography variant="caption" display="block" sx={{ color: "#666", mb: 0.5 }}>
                  <strong>Date:</strong> {approvalDetails.timestamp_according_to_timezone || "-"}
                </Typography>
                <Typography variant="caption" display="block" sx={{ color: "#666", mb: 0.5 }}>
                  <strong>User:</strong> {approvalDetails.user_who_filled_the_data || "-"}
                </Typography>
                {approvalDetails.remarks && (
                  <Typography variant="caption" display="block" sx={{ color: "#666" }}>
                    <strong>Remarks:</strong> {approvalDetails.remarks}
                  </Typography>
                )}
              </Box>

              {/* Modern File Gallery - Responsive Grid */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(70px, 1fr))",
                  gap: 1,
                  mt: 2,
                  width: "100%",
                  maxHeight: 250,
                  overflowY: "auto",
                }}
              >
                {approvalAttachments.map((file, index) => {
                  const isImage = /\.(png|jpe?g|webp|gif|bmp)$/i.test(
                    file.filename || file.file_path,
                  );

                  return (
                    <Box
                      key={file.file_id || `${file.filename}-${index}`}
                      sx={{
                        width: "100%",
                        maxWidth: "100%",
                        border: "1px solid #ddd",
                        borderRadius: 2,
                        p: 1,
                        textAlign: "center",
                        background: "#fafafa",
                        overflow: "hidden",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#e1261c",
                          backgroundColor: "#f0f7ff",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        },
                      }}
                    >
                      {/* Preview Section */}
                      <Box sx={{ mb: 0.5 }}>
                        {isImage ? (
                          <AuthImage
                            filePath={file.file_path}
                            API_URL={API_URL}
                            onView={handleImageOpen}
                            onDownload={downloadWithAuth}
                            filename={file.filename}
                          />
                        ) : null}

                        {/* File Icon for non-images OR fallback */}
                        <Box
                          sx={{
                            height: 60,
                            display: isImage ? "none" : "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "28px",
                            backgroundColor: isImage ? "transparent" : "#e8e8e8",
                            borderRadius: 1,
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            if (isImage) handleImageOpen(null, file.file_path);
                          }}
                        >
                          {!isImage && "📄"}
                        </Box>
                      </Box>

                      {/* Filename */}
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.5,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          width: "100%",
                          fontSize: "11px",
                          color: "#333",
                          fontWeight: 500,
                        }}
                        title={file.filename}
                      >
                        {file.filename}
                      </Typography>

                      {/* Action Buttons */}
                      <Box
                        display="flex"
                        justifyContent="center"
                        gap={0.5}
                        mt={0.5}
                      >
                        <Tooltip title="Preview">
                          <IconButton
                            size="small"
                            onClick={() => {
                              handleImageOpen(null, file.file_path);
                            }}
                            disabled={!isImage}
                            sx={{
                              color: "#e1261c",
                              padding: "4px",
                              "&:hover": {
                                backgroundColor: "rgba(25, 118, 210, 0.1)",
                              },
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => {
                              downloadWithAuth(file.file_path, file.filename);
                            }}
                            sx={{
                              color: "#388e3c",
                              padding: "4px",
                              "&:hover": {
                                backgroundColor: "rgba(56, 142, 60, 0.1)",
                              },
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Box>
        )}

        <Modal open={openImage} onClose={handleImageClose}>
          <Box
            sx={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "transparent",
              p: 1,
              borderRadius: 2,
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              outline: "none",
            }}
          >
            {/* <IconButton
              onClick={handleImageClose}
              sx={{
                position: "absolute",
                top: -40,
                right: 0,
                bgcolor: "rgba(255,255,255,0.9)",
                "&:hover": { bgcolor: "rgba(255,255,255,1)" },
              }}
            >
              <CloseIcon />
            </IconButton> */}
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "90vh",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            )}
          </Box>
        </Modal>

        {/* Download Error Snackbar */}
        <Snackbar
          open={downloadSnack.open}
          autoHideDuration={3500}
          onClose={() => setDownloadSnack({ ...downloadSnack, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert onClose={() => setDownloadSnack({ ...downloadSnack, open: false })} severity="error">
            {downloadSnack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}

// Authenticated thumbnail component for history panel
function AuthImage({ filePath, API_URL, onView, onDownload, filename }) {
  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    const loadImage = async () => {
      try {
        const viewUrl = thumbUrl(API_URL, filePath);
        const token = localStorage.getItem("authKey");

        if (!viewUrl) {
          setLoading(false);
          return;
        }

        const res = await axiosInstance.get(viewUrl, {
          responseType: "blob",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        const blob = new Blob([res.data]);
        objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setImageSrc(objectUrl);
        }
      } catch (err) {
        console.error("History image load error:", filePath, err);
        // Log the attempted URL for debugging
        console.error("Attempted URL:", thumbUrl(API_URL, filePath));
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadImage();

    return () => {
      isMounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [filePath, API_URL]);

  return (
    <Box
      sx={{
        width: 70,
        height: 70,
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "#f2f2f2",
        border: "1px solid #e0e0e0",
        position: "relative",
        "&:hover .overlay": {
          opacity: 1,
          visibility: "visible",
        },
      }}
    >
      {loading ? (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={20} />
        </Box>
      ) : imageSrc ? (
        <img
          src={imageSrc}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          📄
        </Box>
      )}
      <Box
        className="overlay"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(0,0,0,0.5)",
          opacity: 0,
          visibility: "hidden",
          transition: "opacity .2s",
        }}
      >
        <IconButton
          size="small"
          sx={{ color: "#fff" }}
          onClick={() => onView(null, filePath, API_URL)}
        >
          <VisibilityIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          sx={{ color: "#fff" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDownload(filePath, filename);
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
}

function FlangeForm({ isSidebarExpanded }) {
  const { flangeId } = useParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [steps, setSteps] = useState([]);
  const [activeFormName, setActiveFormName] = useState(null);

  const [formValues, setFormValues] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState({});
  const [editModeByKey, setEditModeByKey] = useState({});

  const [loading, setLoading] = useState(true);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "info" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [torqueWarnings, setTorqueWarnings] = useState({});

  const [rawHistory, setRawHistory] = useState([]);
  const [currentUserFromHistory, setCurrentUserFromHistory] = useState("");

  const [openApprovalSection, setOpenApprovalSection] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImageIsObjectUrl, setSelectedImageIsObjectUrl] = useState(false);

  const closeSelectedImage = () => {
    if (selectedImageIsObjectUrl && selectedImage) {
      URL.revokeObjectURL(selectedImage);
    }
    setSelectedImage(null);
    setSelectedImageIsObjectUrl(false);
    setOpenImage(false);
  };

  const approvalData = useMemo(() => {
    // console.log("RAW HISTORY:", rawHistory);
    if (!rawHistory || !Array.isArray(rawHistory)) return null;

    const section = rawHistory.find((x) => Object.keys(x)[0] === "approved");
    const result = section?.approved || null;

    // console.log("APPROVAL DATA:", result);
    return result;
  }, [rawHistory]);

  const approvalAttachments = approvalData?.attachments || [];
  const approvalDetails = approvalData?.details || {};

  // Approval from Client dialog state
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [approvalUploads, setApprovalUploads] = useState([]);
  const [isApprovalSubmitting, setIsApprovalSubmitting] = useState(false);

  const approvalUploadPreviews = useMemo(() => {
    return (approvalUploads || []).map((file) => {
      const isImage = /\.(png|jpe?g|webp|gif|bmp)$/i.test(file.name);
      const url = URL.createObjectURL(file);
      return { file, url, isImage };
    });
  }, [approvalUploads]);

  useEffect(() => {
    return () => {
      approvalUploadPreviews.forEach((item) => {
        try {
          URL.revokeObjectURL(item.url);
        } catch {
          // ignore
        }
      });
    };
  }, [approvalUploadPreviews]);

  const downloadLocalFile = (file) => {
    try {
      const url = URL.createObjectURL(file);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name || "download";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading local attachment", err);
    }
  };

  /** Testing & Retesting lock (UI toggle only) */
  const [testingUnlocked, setTestingUnlocked] = useState(false);

  // NEW: Testing session context
  // const { getRecord, setRecord, clearRecord } = useTestingSession();

  const refreshDetail = useCallback(async () => {
    const authKey = localStorage.getItem("authKey");
    const url = `${API_URL}/api/get_flange_detail_by_id/${flangeId}`;
    const res = await axiosInstance.get(url, {
      headers: { Authorization: `Bearer ${authKey}` },
    });

    setDetail(res.data);
    const isReopened = isFlangeReopened(res.data);
    const tabs = buildStepsFromFormInfo(res.data.form_info, isReopened);
    setSteps(tabs);

    const cf = res.data.form_info?.current_form || "";
    const uiCF = normalizeFormForUI(cf);
    setActiveFormName(uiCF || tabs[0] || ALL_STEPS[0]);
  }, [API_URL, flangeId]);

  // Determine which phase we are in right now for session key
  const backendCurrentForm = detail?.form_info?.current_form || "";

  const activeTestingRecordId =
    detail?.active_testing?.record_id ||
    detail?.tooltip?.active_test_record?.record_id_to_end ||
    null;
  const currentPhase = useMemo(() => {
    return ["Flange Retesting", "Retesting"].includes(backendCurrentForm)
      ? "retesting"
      : "testing";
  }, [backendCurrentForm]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const authKey = localStorage.getItem("authKey");
        const url = `${API_URL}/api/get_flange_detail_by_id/${flangeId}`;
        const res = await axiosInstance.get(url, {
          headers: { Authorization: `Bearer ${authKey}` },
        });
        if (!mounted) return;

        setDetail(res.data);
        const isReopened = isFlangeReopened(res.data);

        // Pass isReopened flag to buildStepsFromFormInfo
        const tabs = buildStepsFromFormInfo(res.data.form_info, isReopened);
        setSteps(tabs);

        const cf = res.data.form_info?.current_form || "";
        const uiCF = normalizeFormForUI(cf);

        // When reopened, only allow current form tab
        if (uiCF) {
          setActiveFormName(uiCF);
        } else {
          // Default to first available tab if no current form
          setActiveFormName(tabs[0] || ALL_STEPS[0]);
        }

        await fetchHistory();
      } catch (e) {
        setSnack({
          open: true,
          msg: e?.response?.data?.message || "Failed to load flange details",
          sev: "error",
        });
        setLoading(false);
        return
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [flangeId]);

  useEffect(() => {
    if (detail?.tooltip?.active_test_record?.record_id_to_end) {
      setTestingUnlocked(true);
    } else {
      setTestingUnlocked(false);
    }
  }, [detail?.tooltip?.active_test_record]);

  const fetchHistory = useCallback(async () => {
    try {
      const authKey = localStorage.getItem("authKey");
      const url = `${API_URL}/api/get_flange_history/${flangeId}`;
      const res = await axiosInstance.get(url, {
        headers: { Authorization: `Bearer ${authKey}` },
      });

      const nextSnapshots = normalizeHistoryToSnapshots(res.data);
      setRawHistory(Array.isArray(res.data?.data) ? res.data.data : []);

      const historyData = res.data?.data || [];
      let userName = "";

      // Check all sections for the latest user
      for (const sectionObj of historyData) {
        const sectionKey = Object.keys(sectionObj || {})[0];
        const entries = sectionObj?.[sectionKey] || [];

        if (Array.isArray(entries) && entries.length > 0) {
          // Get the most recent entry (last one in array)
          const latestEntry = entries[entries.length - 1];
          if (latestEntry?.user_who_filled_the_data) {
            userName = latestEntry.user_who_filled_the_data;
            break; // Found it, stop searching
          }
        }
      }

      setCurrentUserFromHistory(userName);

      setSavedSnapshot((prev) => {
          // console.log("📸 prev.tightening.__existingUploads:", prev.tightening?.__existingUploads);
        const merged = { ...prev };
        Object.keys(nextSnapshots).forEach((k) => {
          merged[k] = mergeSnapshotsPreferNonEmpty(
            prev[k] || {},
            nextSnapshots[k] || {},
          );
        });
        return merged;
      });
    } catch {
      /* ignore */
    }
  }, [flangeId]);

  const activeSchema = useMemo(
    () => getSchemaForFormName(activeFormName),
    [activeFormName],
  );

  useEffect(() => {
    if (activeSchema?.key !== "tightening") {
      setTorqueWarnings({});
    }
  }, [activeSchema?.key]);

  const getProcessNameForSubmit = useCallback(() => {
    const backendCF = detail?.form_info?.current_form || "";
    if (backendCF) return backendCF;
    return activeFormName || "";
  }, [activeFormName, detail?.form_info?.current_form]);

  const setValue = (name, val) => {
    if (!activeSchema?.key) return;
    
    // Special handling for gauge photo uploads
    if (name === "__gauge_photo") {
      // Debug: Log file details when storing
      if (Array.isArray(val) && val.length > 0) {
        // console.log("🔍 Storing Gauge Photo in Form:");
        // console.log("  - Name:", val[0].name);
        // console.log("  - Size:", val[0].size, "bytes");
        // console.log("  - Type:", val[0].type);
      }
      
      setFormValues((prev) => {
        const currentValues = prev[activeSchema.key] || {};
        const snapshot = savedSnapshot[activeSchema.key] || {};
        
        // When uploading a new gauge photo, clear any removed existing gauge photo IDs
        if (Array.isArray(val) && val.length > 0) {
          // Use snapshot's existing gauge IDs since currentValues.__existingGauge might be empty after removal
          const existingGaugeIds = Array.isArray(snapshot.__existingGauge)
            ? snapshot.__existingGauge.map((g) => g.file_id || g.id)
            : [];
          
          // Filter out gauge photo IDs from __removeExistingIds
          const filteredRemoveIds = Array.isArray(currentValues.__removeExistingIds)
            ? currentValues.__removeExistingIds.filter(id => !existingGaugeIds.includes(id))
            : [];
          
          return {
            ...prev,
            [activeSchema.key]: {
              ...currentValues,
              [name]: val,
              __removeExistingIds: filteredRemoveIds
            }
          };
        } else {
          // When clearing gauge photo (val is empty), just update the value
          return {
            ...prev,
            [activeSchema.key]: {
              ...currentValues,
              [name]: val
            }
          };
        }
      });
    } else {
      setFormValues((prev) => ({
        ...prev,
        [activeSchema.key]: { ...(prev[activeSchema.key] || {}), [name]: val },
      }));
    }

    // clear error as soon as user fixes it
    setFieldErrors((prev) => {
      const key = activeSchema?.key;
      if (!key) return prev;
      if (!prev[key] || !(name in (prev[key] || {}))) return prev;
      const nextSec = { ...(prev[key] || {}) };
      delete nextSec[name];
      return { ...prev, [key]: nextSec };
    });

    if (activeSchema?.key === "tightening" && isTorqueInputField(name)) {
      const requiredTorqueValue =
        detail?.required_torque ?? detail?.tooltip?.required_torque;
      const warning = getTorqueWarning(val, requiredTorqueValue);
      setTorqueWarnings((prev) => ({
        ...prev,
        [name]: warning,
      }));
    }
  };

  const handleTorqueBlur = useCallback(
    (fieldName, value) => {
      if (activeSchema?.key !== "tightening") return;
      if (!isTorqueInputField(fieldName)) return;
      const requiredTorqueValue =
        detail?.required_torque ?? detail?.tooltip?.required_torque;
      const warning = getTorqueWarning(value, requiredTorqueValue);
      setTorqueWarnings((prev) => ({
        ...prev,
        [fieldName]: warning,
      }));
    },
    [activeSchema?.key, detail],
  );

  // Validate required fields before submission
  const validateRequiredFields = useCallback(() => {
  if (!activeSchema) return true;

  // ✅ Use displayValues — this is what the user actually sees
  // Use the active section's live state; displayValues is declared later in
  // this component and cannot safely appear in this callback's dependencies.
  const values = formValues[activeSchema.key] || {};
  const nextErrors = {};

  (activeSchema.fields || []).forEach((field) => {
    const isRequired = typeof field.required === "function"
      ? field.required(values)
      : !!field.required;

    if (isRequired) {
      const value = values[field.name];
      const hasExistingGauge =
        field.name === "__gauge_photo" &&
        Array.isArray(values.__existingGauge) &&
        values.__existingGauge.length > 0;
      const isEmpty = Array.isArray(value)
        ? value.length === 0
        : value === undefined || value === null || value === "";

      if (isEmpty && !hasExistingGauge) {
        nextErrors[field.name] = `${field.label || "This field"} is required`;
      }
    }
  });

    // Match the backend tightening rule used by both create and edit:
    // pass values are optional, but when supplied must contain digits only.
    if (activeSchema.key === "tightening") {
      ["pass1", "pass2", "pass3", "pass4"].forEach((fieldName, index) => {
        const value = values[fieldName];
        if (
          value !== undefined &&
          value !== null &&
          String(value).trim() !== "" &&
          !/^\d+$/.test(String(value).trim())
        ) {
          nextErrors[fieldName] = `Pass ${index + 1} must be 0 or greater`;
        }
      });
    }

    const hasErrors = Object.keys(nextErrors).length > 0;

    if (hasErrors) {
      setFieldErrors((prev) => ({
        ...prev,
        [activeSchema.key]: nextErrors,
      }));
      setSnack({
        open: true,
        msg: "Please fill in all required fields",
        sev: "error",
      });
      const firstField = Object.keys(nextErrors)[0];
      requestAnimationFrame(() => {
        const target = document.querySelector(
          `[data-field-name="${firstField}"]`,
        );
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
        target?.querySelector("input, textarea, button")?.focus({
          preventScroll: true,
        });
      });
    }

    return !hasErrors;
  }, [activeSchema, formValues]);

  const showApiSubmissionError = useCallback(
    (error, fallback) => {
      const apiMessage = getApiErrorMessage(error, fallback);
      const fieldName = inferFieldFromApiError(apiMessage);
      const message =
        /^pass[1-4]$/.test(fieldName || "") &&
        /must be an integer/i.test(apiMessage)
          ? `Pass ${fieldName.slice(-1)} must be 0 or greater`
          : apiMessage;
      const isVisibleField = (activeSchema?.fields || []).some(
        (field) => field.name === fieldName,
      );

      if (fieldName && isVisibleField) {
        setFieldErrors((prev) => ({
          ...prev,
          [activeSchema.key]: {
            ...(prev[activeSchema.key] || {}),
            [fieldName]: message,
          },
        }));

        requestAnimationFrame(() => {
          const target = document.querySelector(
            `[data-field-name="${fieldName}"]`,
          );
          target?.scrollIntoView({ behavior: "smooth", block: "center" });
          target?.querySelector("input, textarea, button")?.focus({
            preventScroll: true,
          });
        });
      }

      setSnack({ open: true, msg: message, sev: "error" });
    },
    [activeSchema],
  );

  const buildFormData = useCallback(() => {
    const fd = new FormData();
    fd.append("current_process", getProcessNameForSubmit());

    const values = formValues[activeSchema.key] || {};
    const aliases = activeSchema.aliases || {};

    fd.set("_recordType", activeSchema.key);

    Object.entries(values).forEach(([k, v]) => {
      if (
        k === "__uploads" ||
        k === "__gauge_photo" ||
        k === "__existingFiles" ||
        k === "__recordId"
      )
        return;
      if (v === undefined || v === null || v === "") return;
      const beKey = aliases[k] || k;
      fd.append(beKey, v);
    });

    if (activeSchema.canUpload && Array.isArray(values.__uploads)) {
      values.__uploads.forEach((f) => fd.append("uploads", f));
    }
    if (Array.isArray(values.__gauge_photo) && values.__gauge_photo[0]) {
      const file = values.__gauge_photo[0];
      // console.log("🔍 buildFormData - Gauge Photo File Details:");
      // console.log("  - Name:", file.name);
      // console.log("  - Size:", file.size, "bytes");
      // console.log("  - Type:", file.type);
      // console.log("  - Constructor:", file.constructor.name);
      // console.log("  - Is File object:", file instanceof File);
      // console.log("  - Is Blob object:", file instanceof Blob);
      
      // Validate file before sending
      if (file.size === 0) {
        console.error("❌ ERROR: Gauge photo file is empty (0 bytes)");
        setSnack({
          open: true,
          msg: "The selected gauge photo is empty. Please select a valid image file.",
          sev: "error",
        });
        return null; // Return null to indicate error
      }
      
      fd.set("gauge_photo", values.__gauge_photo[0]); // ✅ Fixed: use set instead of append
      // console.log("🔍 buildFormData - Setting gauge photo:", values.__gauge_photo[0].name);
    }

    // Debug: Log FormData contents
    // console.log("🔍 buildFormData Contents:");
    for (let [key, value] of fd.entries()) {
      // if (value instanceof File) {
        // console.log(`  - ${key}:`, value.name, `(${value.size} bytes)`);
      // } else {
        // console.log(`  - ${key}:`, value);
      // }
    }

    return fd;
  }, [activeSchema, formValues, getProcessNameForSubmit]);

  const buildEditFormData = useCallback(() => {
    const fd = new FormData();

    const values = formValues[activeSchema.key] || {};
    const aliases = activeSchema.aliases || {};
    const snapshot = savedSnapshot[activeSchema.key] || {};

    // ✅ FIX: Use stored record type if available, otherwise infer from activeFormName
    let typeParam = "";

    if (activeSchema.key === "testing") {
      // ✅ NEW: Check snapshot's record type first
      if (snapshot.__recordType === "retesting") {
        typeParam = "retesting";
      } else if (snapshot.__recordType === "testing") {
        typeParam = "testing";
      } else {
        // ✅ Fallback: check backend current form
        if (["Flange Retesting", "Retesting"].includes(backendCurrentForm)) {
          typeParam = "retesting";
        } else if (backendCurrentForm === "Approval from Client") {
          // ✅ FIX for Approval Stage: Check history to see if latest was retesting
          const testedSection = (rawHistory || []).find(
            (x) => Object.keys(x || {})[0] === "tested",
          );
          const testedEntries = testedSection?.tested || [];
          const latestEntry =
            testedEntries.find((e) => e.latest === true) ||
            testedEntries[testedEntries.length - 1];

          if ((latestEntry?.type || "").toLowerCase() === "retesting") {
            typeParam = "retesting";
          } else {
            typeParam = "testing";
          }
        } else {
          typeParam = "testing";
        }
      }
    } else if (snapshot.__recordType) {
      typeParam = snapshot.__recordType;
    } else if (activeSchema.key === "blinding") {
      typeParam = "blinding";
    } else if (activeSchema.key === "deblinding") {
      typeParam = "deblinding";
    } else if (activeSchema.key === "tightening") {
      typeParam = "tightening";
    }

    if (typeParam) {
      fd.append("type", typeParam);
    }
    
    Object.entries(values).forEach(([k, v]) => {
      if (k.startsWith("__") || k === "_recordType") {
        return;
      }
      if (v === undefined || v === null || v === "") return;

      if (k === "remarks") {
        fd.append("remarks", v); // ✅ backend expects remark
      } else {
        const beKey = aliases[k] || k;
        fd.append(beKey, v);
      }
    });

    if (activeSchema.canUpload && Array.isArray(values.__uploads)) {
      values.__uploads.forEach((f) => fd.append("uploads", f));
    }
    if (Array.isArray(values.__gauge_photo) && values.__gauge_photo[0]) {
      const file = values.__gauge_photo[0];
      // console.log("🔍 buildEditFormData - Gauge Photo File Details:");
      // console.log("  - Name:", file.name);
      // console.log("  - Size:", file.size, "bytes");
      // console.log("  - Type:", file.type);
      // console.log("  - Constructor:", file.constructor.name);
      // console.log("  - Is File object:", file instanceof File);
      // console.log("  - Is Blob object:", file instanceof Blob);
      
      // Validate file before sending
      if (file.size === 0) {
        console.error("❌ ERROR: Gauge photo file is empty (0 bytes)");
        setSnack({
          open: true,
          msg: "The selected gauge photo is empty. Please select a valid image file.",
          sev: "error",
        });
        return null; // Return null to indicate error
      }
      
      fd.set("gauge_photo", values.__gauge_photo[0]); // ✅ replace append → set
    }

    // ✅ CRITICAL FIX: Filter out gauge photo IDs when uploading a new gauge photo
    if (
      Array.isArray(values.__removeExistingIds) &&
      values.__removeExistingIds.length
    ) {
      // Check if user is uploading a NEW gauge photo
      const hasNewGaugePhoto =
        Array.isArray(values.__gauge_photo) && values.__gauge_photo.length > 0;

      // Get existing gauge photo IDs to exclude
      const existingGaugeIds = Array.isArray(snapshot.__existingGauge)
        ? snapshot.__existingGauge.map((g) => g.file_id)
        : [];

      values.__removeExistingIds.forEach((id) => {
        if (!id) return;

        // Backend will handle replacing it automatically
        if (hasNewGaugePhoto && existingGaugeIds.includes(id)) {
          return; // Skip gauge photo IDs
        }

        fd.append("files_to_delete", String(id));
      });
    }
    // console.log("🗑️ files_to_delete being sent:");
// for (let [key, value] of fd.entries()) {
//   console.log(`  ${key}:`, value);
// }
    return fd;
  }, [
    activeSchema,
    formValues,
    activeFormName,
    savedSnapshot,
    backendCurrentForm,
    rawHistory,
  ]);

  // ----- Backend calls for testing/retesting start/end -----
  const callStartTesting = useCallback(async () => {
    const authKey = localStorage.getItem("authKey");
    const url = `${API_URL}/api/flange/testing/start/${flangeId}`;
    const res = await axiosInstance.post(
      url,
      {},
      { headers: { Authorization: `Bearer ${authKey}` } },
    );
    return res?.data;
  }, [API_URL, flangeId]);

  const callEndTesting = useCallback(
    async (fd) => {
      const authKey = localStorage.getItem("authKey");

      const url = `${API_URL}/api/flange/testing/ends/${flangeId}`;

      const res = await axiosInstance.post(url, fd, {
        headers: {
          Authorization: `Bearer ${authKey}`,
        },
      });

      return res?.data;
    },
    [API_URL, flangeId],
  );

  const callStartRetesting = useCallback(async () => {
    const authKey = localStorage.getItem("authKey");
    const url = `${API_URL}/api/flange/retesting/start/${flangeId}`;
    const res = await axiosInstance.post(
      url,
      {},
      { headers: { Authorization: `Bearer ${authKey}` } },
    );
    return res?.data;
  }, [API_URL, flangeId]);

  const callEndRetesting = useCallback(
    async (fd) => {
      const authKey = localStorage.getItem("authKey");

      const url = `${API_URL}/api/flange/retesting/ends/${flangeId}`;

      const res = await axiosInstance.post(url, fd, {
        headers: {
          Authorization: `Bearer ${authKey}`,
        },
      });

      return res?.data;
    },
    [API_URL, flangeId],
  );

  const handleSubmit = useCallback(async () => {
    if (!detail || !activeSchema) return;

    // Validate required fields first
    if (!validateRequiredFields()) {
      return;
    }

    if (activeSchema?.key === "tightening") {
      const valuesForSchema = formValues?.[activeSchema.key] || {};
      const requiredTorqueValue =
        detail?.required_torque ?? detail?.tooltip?.required_torque;
      const torqueFieldNames = (activeSchema?.fields || [])
        .filter((f) => f?.type === "number" && isTorqueInputField(f?.name))
        .map((f) => f.name);

      const nextWarnings = {};
      torqueFieldNames.forEach((fieldName) => {
        nextWarnings[fieldName] = getTorqueWarning(
          valuesForSchema[fieldName],
          requiredTorqueValue,
        );
      });

      setTorqueWarnings((prev) => ({ ...prev, ...nextWarnings }));

      const firstWarning = Object.values(nextWarnings).find(Boolean);
      if (firstWarning) {
        setSnack({
          open: true,
          msg: firstWarning,
          sev: "warning",
        });
      }
    }

    try {
      const authKey = localStorage.getItem("authKey");
      const fd = buildFormData();
      
      // Check if buildFormData returned null (error)
      if (!fd) {
        return; // Error already shown in buildFormData
      }
      
      const url = `${API_URL}/api/record_form_data/${flangeId}`;
      const res = await axiosInstance.post(url, fd, {
        headers: {
          Authorization: `Bearer ${authKey}`,
        },
      });

      await fetchHistory();
      await refreshDetail(); // ← This handles everything!

      setSnack({
        open: true,
        msg: res?.data?.message || "Submitted",
        sev: "success",
      });

      setFormValues((prev) => ({
        ...prev,
        [activeSchema.key]: { __uploads: [], __gauge_photo: [] },
      }));
    } catch (e) {
      showApiSubmissionError(e, "Submission failed");
    }
  }, [
    API_URL,
    activeSchema,
    buildFormData,
    detail,
    formValues,
    flangeId,
    fetchHistory,
    showApiSubmissionError,
    validateRequiredFields,
  ]);

  const handleEditSubmit = useCallback(async () => {
    if (!detail || !activeSchema || !activeSchema.editEndpoint) return;

    // Validate required fields first
    if (!validateRequiredFields()) {
      return;
    }

    if (activeSchema?.key === "tightening") {
      const valuesForSchema = formValues?.[activeSchema.key] || {};
      const requiredTorqueValue =
        detail?.required_torque ?? detail?.tooltip?.required_torque;
      const torqueFieldNames = (activeSchema?.fields || [])
        .filter((f) => f?.type === "number" && isTorqueInputField(f?.name))
        .map((f) => f.name);

      const nextWarnings = {};
      torqueFieldNames.forEach((fieldName) => {
        nextWarnings[fieldName] = getTorqueWarning(
          valuesForSchema[fieldName],
          requiredTorqueValue,
        );
      });

      setTorqueWarnings((prev) => ({ ...prev, ...nextWarnings }));

      const firstWarning = Object.values(nextWarnings).find(Boolean);
      if (firstWarning) {
        setSnack({
          open: true,
          msg: firstWarning,
          sev: "warning",
        });
      }
    }

    const values = formValues[activeSchema.key] || {};
    const snapshot = savedSnapshot[activeSchema.key] || {};

    let recordId = snapshot.__recordId || "";
    let recordType = snapshot.__recordType || ""; // "testing" | "retesting"

    if (activeSchema.key === "testing") {
      // If backend current form is retesting stage, force type retesting
      if (["Flange Retesting", "Retesting"].includes(backendCurrentForm)) {
        recordType = "retesting";
      } else if (backendCurrentForm === "Approval from Client") {
        // ✅ FIX: In Approval stage, check history for retesting
        const testedSection = (rawHistory || []).find(
          (x) => Object.keys(x || {})[0] === "tested",
        );
        const testedEntries = testedSection?.tested || [];
        const latestEntry =
          testedEntries.find((e) => e.latest === true) ||
          testedEntries[testedEntries.length - 1];

        if ((latestEntry?.type || "").toLowerCase() === "retesting") {
          recordType = "retesting";
        }
      }

      // ✅ If retesting, ensure recordId is retest_id from history
      if (recordType === "retesting") {
        const testedSection = (rawHistory || []).find(
          (x) => Object.keys(x || {})[0] === "tested",
        );

        const testedEntries = testedSection?.tested || [];

        const latestRetesting =
          testedEntries
            .filter((e) => (e?.type || "").toLowerCase() === "retesting")
            .find((e) => e.latest === true) ||
          testedEntries
            .filter((e) => (e?.type || "").toLowerCase() === "retesting")
            .slice(-1)[0];

        recordId =
          latestRetesting?.retest_id ||
          latestRetesting?.retesting_id ||
          recordId;
      } else {
        // Normal testing fallback
        const testedSection = (rawHistory || []).find(
          (x) => Object.keys(x || {})[0] === "tested",
        );
        const testedEntries = testedSection?.tested || [];
        const latestTesting =
          testedEntries.find((e) => e.latest === true) ||
          testedEntries[testedEntries.length - 1];

        recordId =
          latestTesting?.green_id || latestTesting?.testing_id || recordId;
      }
    }

    // ✅ EXTRA SAFETY: for blinding/deblinding if snapshot missing recordId
    if (!recordId && activeSchema.key === "blinding") {
      const blindSection = (rawHistory || []).find(
        (x) => Object.keys(x || {})[0] === "blinding",
      );
      const blindEntries = blindSection?.blinding || [];
      const latestBlind =
        blindEntries.find((e) => e.latest) ||
        blindEntries[blindEntries.length - 1];

      recordId = latestBlind?.blue_id || latestBlind?.id || "";
    }

    if (!recordId && activeSchema.key === "deblinding") {
      const deblindSection = (rawHistory || []).find(
        (x) => Object.keys(x || {})[0] === "deblinding",
      );
      const deblindEntries = deblindSection?.deblinding || [];
      const latestDeblind =
        deblindEntries.find((e) => e.latest) ||
        deblindEntries[deblindEntries.length - 1];

      recordId = latestDeblind?.red_id || latestDeblind?.id || "";
    }

    // Find correct record ID for testing/retesting
    if (!recordId && activeSchema.key === "testing") {
      const testedSection = (rawHistory || []).find(
        (x) => Object.keys(x || {})[0] === "tested",
      );

      const testedEntries = testedSection?.tested || [];
      const latestEntry = testedEntries.find((e) => e.latest === true);

      if (latestEntry) {
        const latestType = (latestEntry?.type || "").toLowerCase();

        if (latestType === "retesting") {
          recordId =
            latestEntry?.retest_id ||
            latestEntry?.retesting_id ||
            latestEntry?.id ||
            "";
        } else {
          recordId =
            latestEntry?.green_id ||
            latestEntry?.testing_id ||
            latestEntry?.id ||
            "";
        }
      } else {
        const latestTesting = testedEntries.reduce((max, cur) => {
          const c1 = cur?.testing_cycle_number ?? 0;
          const c2 = max?.testing_cycle_number ?? 0;
          return c1 > c2 ? cur : max;
        }, testedEntries[0]);

        if (latestTesting) {
          const latestType = (latestTesting?.type || "").toLowerCase();

          recordId =
            latestType === "retesting"
              ? latestTesting?.retest_id ||
                latestTesting?.retesting_id ||
                latestTesting?.id ||
                ""
              : latestTesting?.green_id ||
                latestTesting?.testing_id ||
                latestTesting?.id ||
                "";
        }
      }
    }

    if (!recordId) {
      setSnack({
        open: true,
        msg: "No existing record found to edit for this form.",
        sev: "warning",
      });
      return;
    }

    const hasAnyChange = Object.keys(values || {}).some(
      (k) =>
        !k.startsWith("__") &&
        values[k] !== "" &&
        values[k] !== null &&
        values[k] !== undefined,
    );

    // if (
    //   !hasAnyChange &&
    //   !(values.__uploads?.length || values.__gauge_photo?.length) &&
    //   !values.__removeExistingIds?.length
    // ) {
    //   setSnack({
    //     open: true,
    //     msg: "No changes to save.",
    //     sev: "warning",
    //   });
    //   return;
    // }

    // ✅ CRITICAL FIX: Add gauge photo validation for testing forms
    // ✅ GAUGE PHOTO VALIDATION: Check if gauge photo was removed during edit
    if (activeSchema.key === "testing") {
      // Check if there's an existing gauge photo in snapshot
      const hasExistingGaugePhoto =
        Array.isArray(snapshot.__existingGauge) &&
        snapshot.__existingGauge.length > 0;

      // Check if there's an existing gauge photo in current values (after successful upload)
      const hasCurrentGaugePhoto =
        Array.isArray(values.__existingGauge) &&
        values.__existingGauge.length > 0;

      // Check if user is uploading a NEW gauge photo
      const hasNewGaugePhoto = !!(
        Array.isArray(values.__gauge_photo) && values.__gauge_photo.length > 0
      );

      // Check if user removed the existing gauge photo
      const removedGaugePhotoIds = Array.isArray(values.__removeExistingIds)
        ? values.__removeExistingIds.filter((id) => {
            // Check if this ID belongs to a gauge photo
            return snapshot.__existingGauge?.some(
              (gauge) =>
                String(gauge.file_id) === String(id) ||
                String(gauge.id) === String(id),
            );
          })
        : [];

      const hasRemovedGaugePhoto = removedGaugePhotoIds.length > 0;

      // ✅ FIXED: Gauge photo is required if it was removed and no new one uploaded
      // Allow edit if:
      // 1. There was no existing gauge photo originally (new record), OR
      // 2. Existing gauge photo is still there (not removed), OR
      // 3. User uploaded a new gauge photo (even if existing was removed)
      // 4. There's a current gauge photo in form values (after successful upload)
      const hasValidGaugePhoto =
        !hasExistingGaugePhoto ||
        (!hasRemovedGaugePhoto && hasExistingGaugePhoto) ||
        hasNewGaugePhoto ||
        hasCurrentGaugePhoto;

      // If we have a valid gauge photo (existing, current, or new), don't show validation error
      if (!hasValidGaugePhoto) {
        setSnack({
          open: true,
          msg: "Gauge photo is mandatory.",
          sev: "error",
        });
        return;
      }

      // ✅ OPTIMIZATION: If we already have a valid gauge photo and no new one is uploaded, 
      // clear the __gauge_photo to prevent unnecessary upload
      if ((hasExistingGaugePhoto || hasCurrentGaugePhoto) && !hasNewGaugePhoto && !hasRemovedGaugePhoto) {
        setFormValues((prev) => ({
          ...prev,
          [activeSchema.key]: {
            ...(prev[activeSchema.key] || {}),
            __gauge_photo: []
          }
        }));
      }
    }

    try {
      const authKey = localStorage.getItem("authKey");
      const fd = buildEditFormData();
      
      // Check if buildEditFormData returned null (error)
      if (!fd) {
        return; // Error already shown in buildEditFormData
      }

      // ✅ ENSURE GAUGE PHOTO IS ALWAYS SENT (either new or existing)
      const hasNewGaugeInForm =
        Array.isArray(values.__gauge_photo) && values.__gauge_photo.length > 0;

      // console.log("🔍 Gauge Photo Debug:");
      // console.log("  - hasNewGaugeInForm:", hasNewGaugeInForm);
      // console.log("  - values.__gauge_photo:", values.__gauge_photo);
      // console.log("  - snapshot.__existingGauge:", snapshot.__existingGauge);

      if (!hasNewGaugeInForm) {
        // ✅ No new gauge photo uploaded, so we MUST send the existing one as a File blob
        const existingGaugePath = snapshot.__existingGauge?.[0]?.file_path;

        if (existingGaugePath) {
          try {
            const parts = existingGaugePath.split(/[\\/]/);
            let downloadUrl = "";

            if (parts.length >= 3) {
              const folder = parts[0];
              const uuid = parts[1];
              const filename = parts.slice(2).join("/");
              downloadUrl = `${API_URL}/view_image/${folder}/${uuid}/${encodeURIComponent(filename)}`;
            } else if (parts.length === 2) {
              downloadUrl = `${API_URL}/view_image/${parts[0]}/${parts[1]}`;
            }

            if (downloadUrl) {
              const imgRes = await axiosInstance.get(downloadUrl, {
                responseType: "blob",
                headers: { Authorization: `Bearer ${authKey}` },
              });

              const fileName = parts[parts.length - 1] || "-";
              const fileObj = new File([imgRes.data], fileName, {
                type: imgRes.data.type || "image/jpeg",
              });

              // ✅ CRITICAL: Set the existing gauge photo as a File object
              fd.set("gauge_photo", fileObj);
              // console.log("  - Set existing gauge photo from path:", existingGaugePath);
            }
          } catch (err) {
            console.error("Failed to fetch existing gauge photo:", err);
            setSnack({
              open: true,
              msg: "Failed to load existing gauge photo. Please upload a new one.",
              sev: "error",
            });
            return;
          }
        }
      } else {
        // console.log("  - New gauge photo will be sent:", values.__gauge_photo[0].name);
      }

      let editEndpoint = activeSchema.editEndpoint;

      if (activeSchema.key === "testing") {
        editEndpoint = "edit_testing_form";
      } else if (activeSchema.key === "tightening") {
        editEndpoint = "edit_tightening_form";
      }

      const url = `${API_URL}/api/${editEndpoint}/${recordId}`;

      // Debug: Log FormData contents
      console.log("🔍 FormData Contents:");
      for (let [key, value] of fd.entries()) {
        if (value instanceof File) {
          // console.log(`  - ${key}:`, value.name, `(${value.size} bytes)`);
        } else {
          // console.log(`  - ${key}:`, value);
        }
      }

      const res = await axiosInstance.put(url, fd, {
        headers: {
          Authorization: `Bearer ${authKey}`,
          "Content-Type": "multipart/form-data",
        },
      });

      setSavedSnapshot((prev) => {
        const curr = prev?.[activeSchema.key] || {};
        const next = { ...curr };

        if (values.remarks !== undefined) next.remarks = values.remarks;
        if (values.pass1 !== undefined) next.pass1 = values.pass1;
        if (values.pass2 !== undefined) next.pass2 = values.pass2;
        if (values.pass3 !== undefined) next.pass3 = values.pass3;
        if (values.pass4 !== undefined) next.pass4 = values.pass4;
        if (values.gap_between_faces !== undefined)
          next.gap_between_faces = values.gap_between_faces;
        if (values.is_extra_tightening !== undefined)
          next.is_extra_tightening = values.is_extra_tightening;
        if (values.tightening_remark !== undefined)
          next.tightening_remark = values.tightening_remark;
        if (values.pressure_value !== undefined)
          next.pressure_value = values.pressure_value;
        if (values.test_result !== undefined)
          next.test_result = values.test_result;

        // ✅ IMPORTANT: Update __existingGauge if a new gauge photo was uploaded
        if (Array.isArray(values.__gauge_photo) && values.__gauge_photo.length > 0) {
          // Clear old gauge photos and add the new one
          next.__existingGauge = [{
            file_id: null, // Will be updated by backend response
            file_path: null, // Will be updated by backend response
            filename: values.__gauge_photo[0].name,
            file_size: values.__gauge_photo[0].size,
            is_new: true // Flag to indicate this is a newly uploaded file
          }];
        }

        return { ...prev, [activeSchema.key]: next };
      });
      
      await fetchHistory();
      
      // console.log("🔄 savedSnapshot after fetchHistory:", savedSnapshot.tightening?.__existingUploads);
      // Clear uploaded files from form values after successful submission
      setFormValues((prev) => ({
        ...prev,
        [activeSchema.key]: {
          ...(prev[activeSchema.key] || {}),
          __uploads: [],
          __gauge_photo: [],
          __removeExistingIds: []
        }
      }));

      setEditModeByKey((prev) => ({
        ...prev,
        [activeSchema.key]: false,
      }));

      await refreshDetail();

      setSnack({
        open: true,
        msg: res?.data?.message || "Edited successfully",
        sev: "success",
      });
    } catch (e) {
      showApiSubmissionError(
        e,
        "Edit failed. Please ensure this form was previously submitted.",
      );
    }
  }, [
    API_URL,
    activeSchema,
    buildEditFormData,
    detail,
    savedSnapshot,
    formValues,
    fetchHistory,
    refreshDetail,
    rawHistory,
    showApiSubmissionError,
    validateRequiredFields,
  ]);

  const handleActionButton = useCallback(async () => {
    const rawLabel = detail?.form_info?.action_button || "";
    const label = rawLabel.trim().toLowerCase();
    const backendCF = detail?.form_info?.current_form || "";

    const inTestingTab = activeFormName === "Flange Testing";
    const isApprovalStage = backendCF === "Approval from Client";
    const isRetestingTestingStage =
      backendCF === "Flange Retesting" || backendCF === "Retesting";
    const isRetighteningStage = backendCF === "Flange Retightening";

    // ---- Handle Retesting button click - use backend API ----
    if (inTestingTab && /^retesting$/i.test(rawLabel.trim())) {
      try {
        const authKey = localStorage.getItem("authKey");
        const fd = new FormData();
        // fd.append("date", new Date().toISOString().slice(0, 10));
        fd.append("current_process", backendCF); // Use actual backend current_form
        // fd.append("status", "Retesting");

        const url = `${API_URL}/api/record_form_data/${flangeId}`;
        const res = await axiosInstance.post(url, fd, {
          headers: {
            Authorization: `Bearer ${authKey}`,
          },
        });

        // Refresh detail from backend to get updated flow
        await refreshDetail();
        await fetchHistory();

        setSnack({
          open: true,
          msg: res?.data?.message || "Retesting process initiated",
          sev: "success",
        });
      } catch (e) {
        setSnack({
          open: true,
          msg: e?.response?.data?.message || "Failed to initiate retesting",
          sev: "error",
        });
      }
      return;
    }

    const isStartLike =
      /^(start\s*)?(re)?testing$/.test(label) && !/^end\s*/.test(label);
    const isEndLike = /^end\s*(re)?testing/.test(label);

    // ---------- START Testing / Retesting ----------
    if (inTestingTab && isStartLike) {
      try {
        const data = isRetestingTestingStage
          ? await callStartRetesting()
          : await callStartTesting();

        // ✅ CRITICAL: Refresh detail IMMEDIATELY after starting
        // This populates record_id_to_end in detail state
        await refreshDetail();
        await fetchHistory();

        setTestingUnlocked(true);

        const activeRecordId =
          detail?.tooltip?.active_test_record?.record_id_to_end ||
          data?.record_id ||
          null;

        if (activeRecordId) {
          localStorage.setItem(
            TESTING_SESSION_KEY(flangeId),
            JSON.stringify({
              record_id_to_end: activeRecordId,
              phase: isRetestingTestingStage ? "retesting" : "testing",
              started_at: new Date().toISOString(),
            }),
          );
        }

        // if (data?.record_id) {
        //   setRecord({
        //     flangeId,
        //     phase: isRetestingTestingStage ? "retesting" : "testing",
        //     recordId: data.record_id,
        //     meta: { startedAt: new Date().toISOString() },
        //   });
        // }

        // ✅ NEW: Clear form values when starting retesting
        if (isRetestingTestingStage) {
          setFormValues((prev) => ({
            ...prev,
            testing: {
              pressure_value: "",
              test_result: "",
              remarks: "",
              __uploads: [],
              __gauge_photo: [],
              __existingGauge: [],
              __existingUploads: [],
            },
          }));
        }

        setSnack({
          open: true,
          msg: data?.message || "Started",
          sev: "success",
        });
      } catch (e) {
        setSnack({
          open: true,
          msg: e?.response?.data?.message || "Failed to start",
          sev: "error",
        });
      }
      return;
    }

    // ---------- END Testing / Retesting ----------
    if (inTestingTab && isEndLike) {
      if (!validateRequiredFields()) return;

      try {
        const testingVals = formValues.testing || {};

        const recordId =
          detail?.tooltip?.active_test_record?.record_id_to_end || null;

        // ✅ Fallback to localStorage (after refresh / reload)
        if (!recordId) {
          try {
            const stored = JSON.parse(
              localStorage.getItem(TESTING_SESSION_KEY(flangeId)) || "{}",
            );
            recordId = stored.record_id_to_end || null;
          } catch {
            recordId = null;
          }
        }

        if (!recordId) {
          setSnack({
            open: true,
            sev: "error",
            msg: "No active testing session found. Please start testing first.",
          });
          return;
        }

        // ✅ Build FormData with testing-specific fields
        const fd = new FormData();

        // ✅✅ ADD THIS LINE (REQUIRED)
        fd.append("current_process", backendCF);

        // fd.append("record_id_to_end", recordId);
        fd.append("record_id", recordId);
        if (testingVals.pressure_value) {
          fd.append("pressure_value", testingVals.pressure_value);
        }
        if (testingVals.test_result) {
          fd.append("test_result", testingVals.test_result);
        }
        if (testingVals.remarks) {
          fd.append("remark", testingVals.remarks);
        }

        if (
          Array.isArray(testingVals.__gauge_photo) &&
          testingVals.__gauge_photo[0]
        ) {
          fd.set("gauge_photo", testingVals.__gauge_photo[0]);
        }

        if (Array.isArray(testingVals.__uploads)) {
          testingVals.__uploads.forEach((f) => fd.append("uploads", f));
        }

        const data = ["Flange Retesting", "Retesting"].includes(backendCF)
          ? await callEndRetesting(fd)
          : await callEndTesting(fd);

        // ✅ CLEAR testing session after successful END
        localStorage.removeItem(TESTING_SESSION_KEY(flangeId));

        setTestingUnlocked(false);

        await refreshDetail();
        await fetchHistory();

        setFormValues((prev) => ({
          ...prev,
          testing: {
            pressure_value: "",
            test_result: "",
            remarks: "",
            __uploads: [],
            __gauge_photo: [],
            __existingGauge: [],
            __existingUploads: [],
          },
        }));

        setSnack({
          open: true,
          msg: data?.message || "Testing ended successfully",
          sev: "success",
        });
      } catch (e) {
        console.error("❌ End Testing Error:", e);
        setSnack({
          open: true,
          msg: e?.response?.data?.message || "Failed to end testing",
          sev: "error",
        });
      }
      return;
    }

    if (isApprovalStage) {
      setApprovalDialogOpen(true);
      return;
    }

    if (isRetighteningStage && activeFormName === "Flange Tightening") {
      await handleSubmit();
      return;
    }

    await handleSubmit();
  }, [
    API_URL,
    activeFormName,
    backendCurrentForm,
    buildEditFormData, // ✅ ADD THIS (you use it in END Testing)
    callEndRetesting,
    callEndTesting,
    callStartRetesting,
    callStartTesting,
    detail?.form_info?.action_button,
    detail?.form_info?.current_form,
    detail?.tooltip?.active_test_record?.record_id_to_end, // ✅ ADD THIS
    fetchHistory,
    flangeId,
    formValues.testing,
    handleSubmit,
    rawHistory, // ✅ ADD THIS (you use it in END Testing fallback)
    refreshDetail,
    validateRequiredFields,
  ]);

  /** Handle Approval from Client submission */
  const handleApprovalSubmit = useCallback(
    async (remarks, uploads) => {
      try {
        setIsApprovalSubmitting(true);
        const authKey = localStorage.getItem("authKey");
        const fd = new FormData();

        fd.append("current_process", "Approval from Client");
        if (remarks) {
          fd.append("remark", remarks);
        }

        // Add file uploads
        if (Array.isArray(uploads) && uploads.length > 0) {
          uploads.forEach((file) => {
            fd.append("uploads", file);
          });
        }

        const url = `${API_URL}/api/record_form_data/${flangeId}`;
        const res = await axiosInstance.post(url, fd, {
          headers: {
            Authorization: `Bearer ${authKey}`,
          },
        });

        await refreshDetail();
        await fetchHistory();

        setApprovalDialogOpen(false);
        setApprovalRemarks("");
        setApprovalUploads([]);

        setSnack({
          open: true,
          msg: res?.data?.message || "Approval submitted successfully",
          sev: "success",
        });
      } catch (e) {
        // console.log("❌ Approval Submission Error:", e?.response?.data?.message);
        setSnack({
          open: true,
          msg: e?.response?.data?.message || "Failed to submit approval",
          sev: "error",
        });
      } finally {
        setIsApprovalSubmitting(false);
      }
    },
    [API_URL, flangeId, refreshDetail, fetchHistory],
  );

  /** ---------- FLAGS ---------- */
  const uiCurrentForm = normalizeFormForUI(backendCurrentForm);

  const isRetestingNotStarted =
    activeFormName === "Flange Testing" &&
    ["Flange Retesting", "Retesting"].includes(backendCurrentForm) &&
    !testingUnlocked;

  const isCurrentForm =
    (activeFormName === "Flange Tightening" &&
      backendCurrentForm === "Flange Retightening") ||
    // Normal testing
    (activeFormName === "Flange Testing" &&
      backendCurrentForm === "Flange Testing") ||
    // Retesting becomes editable ONLY after Start Testing
    (activeFormName === "Flange Testing" &&
      ["Flange Retesting", "Retesting"].includes(backendCurrentForm) &&
      testingUnlocked) ||
    // ❌ IMPORTANT: explicitly exclude retesting-not-started
    (!isRetestingNotStarted &&
      backendCurrentForm !== "Approval from Client" &&
      activeFormName === uiCurrentForm);

  // 1. FIRST: Declare actionButtonLabel
  const actionButtonLabel =
    activeFormName === "Flange Testing"
      ? detail?.form_info?.action_button
      : null;

  const showActionButton = !!actionButtonLabel;

  const isTestingTab = activeFormName === "Flange Testing";
  const actionText = (detail?.form_info?.action_button || "").toLowerCase();
  const isStartLikeNow =
    (/^start\s*(re)?testing/.test(actionText) ||
      /^(re)?testing$/.test(actionText)) &&
    !/^end\s*/.test(actionText);

  // If there is an active session in context, treat as unlocked
  // useEffect(() => {
  //   const rec = getRecord({ flangeId, phase: currentPhase });
  //   setTestingUnlocked(!!rec);
  // }, [getRecord, flangeId, currentPhase, rawHistory, backendCurrentForm]);

  const testingOngoing =
    activeFormName === "Flange Testing" &&
    actionButtonLabel?.toLowerCase().startsWith("end");

  const isEditMode = !!editModeByKey[activeSchema?.key];

  const shouldLockTesting =
    isTestingTab && isStartLikeNow && !testingUnlocked && !isEditMode;

  // const lockTestResult =
  //   isEditMode &&
  //   activeFormName === "Flange Testing" &&
  //   ["Flange Retesting", "Retesting"].includes(backendCurrentForm);
  // const lockTestResult = isEditMode && activeSchema?.editEndpoint === "edit_testing_form";
  const lockFieldsInTestingEdit = isEditMode && activeSchema?.key === "testing";

  const isRetestingViewOnly =
    activeSchema?.key === "testing" &&
    ["Flange Retesting", "Retesting"].includes(backendCurrentForm) &&
    !testingUnlocked;

  const hasSavedDataForActive = !!(
    savedSnapshot[activeSchema?.key] &&
    Object.keys(savedSnapshot[activeSchema?.key] || {}).some(
      (key) => !key.startsWith("__"),
    )
  );

  const showHeaderEdit =
    !isCurrentForm && !isEditMode && hasSavedDataForActive;

  const displayValues = useMemo(() => {
    if (!activeSchema) return {};
  //   console.log("🖥️ displayValues computing for:", activeSchema?.key);
  // console.log("  isCurrentForm:", isCurrentForm);
  // console.log("  isEditMode:", isEditMode);
  // console.log("  shouldLockTesting:", shouldLockTesting);
  // console.log("  formValues[key].__existingUploads:", formValues[activeSchema?.key]?.__existingUploads);
  // console.log("  savedSnapshot[key].__existingUploads:", savedSnapshot[activeSchema?.key]?.__existingUploads);
    // 🔥 CASE 1: Flange Retesting BEFORE Start → show EMPTY form
    if (
      activeSchema.key === "testing" &&
      ["Flange Retesting"].includes(backendCurrentForm) &&
      !testingUnlocked &&
      !isEditMode
    ) {
      return {};
    }

    if (
      activeSchema.key === "testing" &&
      ["Retesting"].includes(backendCurrentForm) &&
      !testingUnlocked &&
      !isEditMode
    ) {
      return savedSnapshot?.testing || {};
    }

    // 🔒 CASE 2: Testing locked (before start in normal testing)
    if (isCurrentForm && shouldLockTesting) {
      return savedSnapshot[activeSchema.key] || {};
    }

    // ✏️ CASE 3: Active form or edit mode
    if (isCurrentForm || isEditMode) {
      return formValues[activeSchema.key] || {};
    }

    // 📌 DEFAULT: show snapshot
    return savedSnapshot[activeSchema.key] || {};
  }, [
    activeSchema,
    backendCurrentForm,
    testingUnlocked,
    isEditMode,
    isCurrentForm,
    shouldLockTesting,
    formValues,
    savedSnapshot,
  ]);

  const fieldAssistMessages = useMemo(() => {
    if (activeSchema?.key !== "tightening") return {};
    const next = {};
    Object.entries(torqueWarnings || {}).forEach(([fieldName, warning]) => {
      if (!warning) return;
      next[fieldName] = {
        text: warning,
        severity: "warning",
      };
    });
    return next;
  }, [activeSchema?.key, torqueWarnings]);

  const historySectionKey = formNameToHistorySection(activeFormName);
  const historyEntriesForActive = useMemo(() => {
    if (!historySectionKey) return [];
    const sectionObj =
      (rawHistory || []).find(
        (x) => Object.keys(x || {})[0] === historySectionKey,
      ) || null;
    const list =
      sectionObj && Array.isArray(sectionObj[historySectionKey])
        ? sectionObj[historySectionKey]
        : [];
    return list; // include both tightening + retightening; ordering handled in panel
  }, [historySectionKey, rawHistory]);

  /** Determine user name and signed based on history availability */
  const dynamicHeaderData = useMemo(() => {
    // Try to get user from history using schema key
    const historyUser = getUserFromHistory(rawHistory, activeSchema?.key);

    // If history exists for this form, use user from history
    // Otherwise, use the logged-in user from detail API
    const userName = historyUser || detail?.user_filling_the_form;

    return {
      equipment_number: detail?.equipment_number,
      method_of_tightening: detail?.method_of_tightening,
      user_filling_the_form: userName,
      signed: userName ?? "",
    };
  }, [detail, activeSchema?.key, rawHistory]);

  const handleEnterEditMode = useCallback(() => {
    const snapshot = savedSnapshot[activeSchema?.key];
    if (!snapshot) {
      setSnack({
        open: true,
        msg: "No saved data found to edit.",
        sev: "warning",
      });
      return;
    }

    // ✅ CRITICAL FIX: Preserve __existingGauge and __existingUploads
    setFormValues((prev) => ({
      ...prev,
      [activeSchema.key]: {
        ...snapshot,
        __removeExistingIds: [],
        // ✅ Explicitly preserve existing files
        __existingGauge: snapshot.__existingGauge || [],
        __existingUploads: snapshot.__existingUploads || [],
      },
    }));

    setEditModeByKey((prev) => ({
      ...prev,
      [activeSchema.key]: true,
    }));
  }, [activeSchema, savedSnapshot]);

  const handleCancelEdit = useCallback(() => {
    // Reset form values to the saved snapshot
    const snapshot = savedSnapshot[activeSchema?.key];
    if (snapshot) {
      setFormValues((prev) => ({
        ...prev,
        [activeSchema.key]: {
          ...snapshot,
          __removeExistingIds: [],
          __existingGauge: snapshot.__existingGauge || [],
          __existingUploads: snapshot.__existingUploads || [],
        },
      }));
    }

    // Exit edit mode
    setEditModeByKey((prev) => ({
      ...prev,
      [activeSchema.key]: false,
    }));
  }, [activeSchema, savedSnapshot]);

  if (loading) {
    return (
      <Box
        display="flex"
        minHeight={320}
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress />
      </Box>
    );
  }
  if (!detail)
    return <Alert severity="error">Unable to load this flange.</Alert>;

  const showHistory =
    activeFormName === "Flange Tightening" ||
    activeFormName === "Flange Testing";

  const unlockedIdx = calcUnlockedIndex(detail?.form_info || {}, {
    forceFromCurrentOnly: isFlangeReopened(detail),
  });

  const handleTabChange = (e, nextTabName) => {
    // Block tab changes if flange is reopened and trying to access non-current form
    const reopened = isFlangeReopened(detail);
    if (reopened) {
      const currentForm = normalizeFormForUI(detail?.form_info?.current_form);
      if (nextTabName !== currentForm) {
        setSnack({
          open: true,
          sev: "error",
          msg: "Please complete the current form before accessing other tabs.",
        });
        e.preventDefault?.();
        return;
      }
    }

    const idx = ALL_STEPS.indexOf(nextTabName);
    const unlockedIdx = calcUnlockedIndex(detail?.form_info || {}, {
      forceFromCurrentOnly: reopened,
    });

    if (idx > unlockedIdx) {
      const requiredForm = ALL_STEPS[unlockedIdx] || "current form";
      setSnack({
        open: true,
        sev: "info",
        msg: `${nextTabName} is locked. Complete ${requiredForm} to unlock it.`,
      });
      e.preventDefault?.();
      return;
    }

    setEditModeByKey({});
    setActiveFormName(nextTabName);
  };

  return (
    <Box
      className={`table-container flange-form-page ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}
    >
      <div className="flange-form-sticky-header">
        <Tooltip title="Back to All Flanges" placement="right" arrow>
          <IconButton
            className="flange-form-back-button"
            onClick={() => navigate("/all-flanges")}
            aria-label="Back to All Flanges"
            sx={{
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.08)" },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        {/* Custom Tabs */}
        <div className="Tabs flange-form-tabs">
          <div className="mui-tabs">
          {steps.map((name) => {
            const idx = ALL_STEPS.indexOf(name);
            const reopened = isFlangeReopened(detail);
            const isLocked = reopened
              ? name !== normalizeFormForUI(detail?.form_info?.current_form)
              : idx > unlockedIdx;
            const currentForm = normalizeFormForUI(
              detail?.form_info?.current_form,
            );

            // When reopened, only current form should be shown and enabled
            if (reopened && name !== currentForm) {
              return null;
            }

            // For reopened flanges, NEVER show ticks regardless of previous_forms
            const showTick =
              !reopened &&
              (detail?.form_info?.previous_forms || []).includes(name) &&
              name !== currentForm;

            return (
              <button
                key={name}
                className={`custom-tab new-css-tab ${
                  activeFormName === name ? "active-tab" : ""
                } ${isLocked ? "locked-tab" : ""}`}
                onClick={(e) => handleTabChange(e, name)}
                aria-disabled={isLocked}
                title={
                  isLocked
                    ? `Complete ${ALL_STEPS[unlockedIdx] || "the current form"} to unlock ${name}`
                    : undefined
                }
              >
                {name.toUpperCase()}
                {/* {showTick && (
                  <span style={{ color: "#4CAF50", marginLeft: "4px" }}>✓</span>
                )} */}
              </button>
            );
          })}
          </div>
        </div>
      </div>

      {/* Bootstrap 5 Grid Layout */}
      <div className={`row flange-form-workspace${showHistory ? " has-history" : ""}`}>
        {/* Left Side - Form (9 columns) */}
        <div
          className={`flange-form-main-column col-lg-${showHistory ? 9 : 12} col-md-${
            showHistory ? 8 : 12
          }`}
        >
          <Card className="flange-form-card" elevation={3}>
            <CardHeader
              action={
                showHeaderEdit ? (
                  <Button
                    className="flange-form-header-edit"
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={handleEnterEditMode}
                  >
                    Edit form
                  </Button>
                ) : null
              }
              title={
                <Tooltip
                  title={
                    detail?.tooltip ? (
                      <Box
                        sx={{
                          maxWidth: 420,
                          maxHeight: 320,
                          overflowY: "auto",
                          p: 2.5,
                          background:
                            "linear-gradient(135deg, #ffffff 0%, #fefaf6 100%)",
                          borderRadius: 2,
                          boxShadow:
                            "0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.08)",
                          color: "#1f2937",
                          backdropFilter: "blur(10px)",
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            background: "rgba(0, 0, 0, 0.05)",
                            borderRadius: "3px",
                          },
                          "&::-webkit-scrollbar-thumb": {
                            background: "rgba(0, 0, 0, 0.2)",
                            borderRadius: "3px",
                            "&:hover": {
                              background: "rgba(0, 0, 0, 0.3)",
                            },
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: "bold",
                            mb: 2.5,
                            fontSize: "1.1rem",
                            borderBottom: "2px solid #e1261c",
                            pb: 1,
                            color: "#1f2937",
                          }}
                        >
                          Flange Details
                        </Typography>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            rowGap: 1.5,
                            columnGap: 3,
                          }}
                        >
                          {[
                            [
                              "Flange Size",
                              `${detail.tooltip.flange_size || "-"} ${
                                detail.tooltip.flange_size_unit || ""
                              }`,
                            ],
                            ["Flange Type", detail.tooltip.flange_type || "-"],
                            ["Rating", detail.tooltip.rating || "-"],
                            [
                              "Number of Studs",
                              detail.tooltip.number_of_studs || "-",
                            ],
                            [
                              "Stud Size",
                              `${detail.tooltip.stud_size || "-"} ${
                                detail.tooltip.stud_size_unit || ""
                              }`,
                            ],
                            [
                              "Stud Length",
                              `${detail.tooltip.stud_length || "-"} ${
                                detail.tooltip.stud_length_unit || ""
                              }`,
                            ],
                            [
                              "Stud/Nut MOC",
                              detail.tooltip.stud_nut_moc || "-",
                            ],
                            [
                              "Spanner Size",
                              `${detail.tooltip.spanner_size || "-"} ${
                                detail.tooltip.spanner_size_unit || ""
                              }`,
                            ],
                            [
                              "Lubricant Used",
                              detail.tooltip.lubricant_used || "-",
                            ],
                            ["Gasket MOC", detail.tooltip.gasket_moc || "-"],
                          ].map(([label, value], i) => (
                            <React.Fragment key={i}>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#374151",
                                  fontSize: "0.875rem",
                                  py: 0.5,
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                                }}
                              >
                                {label}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "#6b7280",
                                  fontSize: "0.875rem",
                                  py: 0.5,
                                  borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                                }}
                              >
                                {value}
                              </Typography>
                            </React.Fragment>
                          ))}
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: "#fefaf6",
                          borderRadius: 1,
                          border: "1px solid rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          No data available
                        </Typography>
                      </Box>
                    )
                  }
                  placement="right"
                  arrow
                  slotProps={{
                    popper: {
                      modifiers: [
                        {
                          name: "offset",
                          options: {
                            offset: [0, 10],
                          },
                        },
                      ],
                    },
                    tooltip: {
                      sx: {
                        bgcolor: "transparent",
                        maxWidth: "none",
                        p: 0,
                      },
                    },
                    arrow: {
                      sx: {
                        color: "#ffffff",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        color: "#e1261c",
                        "&:hover": {
                          color: "#c91f17",
                          transform: "scale(1.2) rotate(15deg)",
                          filter:
                            "drop-shadow(0 0 6px rgba(25, 118, 210, 0.5))",
                        },
                      }}
                    >
                      <IoIosInformationCircleOutline size={22} />
                    </Box>
                    <Typography
                      variant="h6"
                      component="span"
                      sx={{
                        color: "#333",
                        fontWeight: 500,
                      }}
                    >
                      Tag No:{" "}
                      {detail.tag_number || detail.equipment_number || "N/A"}
                    </Typography>
                  </Box>
                </Tooltip>
              }
            />

            <Divider />
            <CardContent className="flange-form-scroll-content">
              <FormComponent
                backendCurrentForm={backendCurrentForm}
                schema={activeSchema}
                headerData={dynamicHeaderData}
                values={displayValues}
                onChange={setValue}
                onFieldBlur={handleTorqueBlur}
                isCurrentForm={isCurrentForm}
                isEditMode={isEditMode}
                onEditModeChange={handleEnterEditMode}
                onSubmit={isEditMode ? handleEditSubmit : handleSubmit}
                actionButtonLabel={actionButtonLabel}
                onActionButton={handleActionButton}
                API_URL={API_URL}
                forceDisabled={shouldLockTesting}
                hideSubmit={activeSchema?.hideSubmit}
                hasSavedData={hasSavedDataForActive}
                hideEditButton={showHeaderEdit}
                errors={fieldErrors[activeSchema?.key] || {}}
                fieldAssistMessages={fieldAssistMessages}
                // lockTestResult={lockTestResult}
                lockTestResultOnly={
                  isEditMode && activeSchema?.key === "testing"
                }
                testingOngoing={testingOngoing}
                onCancelEdit={handleCancelEdit}
                onLockedInteraction={() =>
                  setSnack({
                    open: true,
                    sev: "info",
                    msg: "Please start testing first. The fields will be editable after testing starts.",
                  })
                }
              />
            </CardContent>
          </Card>
        </div>

        {/* Right Side - History (3 columns) */}
        {showHistory && (
          <div className="col-lg-3 col-md-4 flange-history-column">
                <HistoryPanel
                  sectionEntries={historyEntriesForActive}
                  API_URL={API_URL}
                  currentForm={backendCurrentForm}
                  approvalAttachments={activeFormName === "Flange Testing" ? approvalAttachments : []}
                  approvalDetails={activeFormName === "Flange Testing" ? approvalDetails : {}}
                />
          </div>
      )}
    </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Image preview modal (global) */}
      <Modal open={openImage} onClose={closeSelectedImage}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            maxWidth: "90%",
            maxHeight: "90%",
            p: 1,
            outline: "none",
          }}
        >
          <img
            src={selectedImage}
            alt="approval preview"
            style={{ width: "100%", height: "auto", maxHeight: "80vh" }}
          />
        </Box>
      </Modal>

      {/* Approval from Client Dialog */}
      <Modal
        open={approvalDialogOpen}
        onClose={() => !isApprovalSubmitting && setApprovalDialogOpen(false)}
      >
        <Box
          className="approval-client-dialog"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 600,
            bgcolor: "background.paper",
            borderRadius: "8px",
            boxShadow: "0 18px 48px rgba(17, 24, 39, 0.22)",
            p: 4,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight="bold">
              Approval from Client
            </Typography>
            <IconButton
              onClick={() => !isApprovalSubmitting && setApprovalDialogOpen(false)}
              disabled={isApprovalSubmitting}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Remarks input */}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks"
            value={approvalRemarks}
            onChange={(e) => setApprovalRemarks(e.target.value)}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#c91f17",
                borderWidth: "1px",
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#c91f17" },
            }}
            disabled={isApprovalSubmitting}
          />

          {/* File upload */}
          <Box mb={3}>
            <Typography variant="subtitle2" fontWeight="600" mb={1}>
              Upload Documents
            </Typography>
            <Box
              sx={{
                border: "2px dashed #e1261c",
                borderRadius: 1.5,
                p: 2,
                textAlign: "center",
                cursor: isApprovalSubmitting ? "not-allowed" : "pointer",
                backgroundColor: "#f9f9f9",
                width: "100%",
                transition: "all 0.2s",
                "&:hover": !isApprovalSubmitting && {
                  backgroundColor: "#fff6f5",
                  borderColor: "#c91f17",
                },
              }}
              component="label"
              htmlFor="approval-file-input"
            >
              <input
                hidden
                id="approval-file-input"
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setApprovalUploads((prev) => [...prev, ...files]);
                }}
                disabled={isApprovalSubmitting}
                
              />
              <UploadIcon sx={{ fontSize: 40, color: "#e1261c", mb: 1 }} />
              <Typography variant="body2">
                Click to upload or drag and drop files
              </Typography>
            </Box>

            {/* Display uploaded files */}
            {approvalUploads.length > 0 && (
              <Box mt={2}>
                <Typography variant="caption" fontWeight="600">
                  {approvalUploads.length} file(s) selected:
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  {approvalUploadPreviews.map((item, idx) => {
                    const { file, url, isImage } = item;
                    return (
                      <Paper
                        key={`${file.name}-${idx}`}
                        elevation={1}
                        sx={{
                          width: 150,
                          p: 1,
                          borderRadius: 1,
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            height: 90,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#f2f2f2",
                            borderRadius: 1,
                            overflow: "hidden",
                          }}
                        >
                          {isImage ? (
                            <img
                              src={url}
                              alt={file.name}
                              style={{ width: "100%", height: "100%", objectFit: "cover", cursor: "pointer" }}
                              onClick={() => {
                                setSelectedImage(url);
                                setSelectedImageIsObjectUrl(false);
                                setOpenImage(true);
                              }}
                            />
                          ) : (
                            <Typography variant="h4">📄</Typography>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          noWrap
                          title={file.name}
                          sx={{ mt: 0.5, display: "block", width: "100%" }}
                        >
                          {file.name}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                          {isImage && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedImage(url);
                                setSelectedImageIsObjectUrl(false);
                                setOpenImage(true);
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => downloadLocalFile(file)}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setApprovalUploads((prev) => prev.filter((_, i) => i !== idx));
                            }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            )}
          </Box>

          {/* Dialog Actions */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              className="approval-dialog-cancel"
              onClick={() => setApprovalDialogOpen(false)}
              disabled={isApprovalSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className="approval-dialog-submit"
              onClick={() => handleApprovalSubmit(approvalRemarks, approvalUploads)}
              disabled={isApprovalSubmitting}
              startIcon={
                isApprovalSubmitting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : null
              }
            >
              Submit Approval
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}

export default FlangeForm;
